import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { openChatWindow } from '@features/front-chat';
import { useClinicians } from '@features/organizations';
import {
  ArtifactDropOverlay,
  formatUploadLimitError,
  useAdminArtifactUploadConstraints,
  useAdminOrganizationArtifactEventStream,
  useArtifactDownload,
  useArtifactUploadConstraints,
  useChartWideDropZone,
  useOptimisticUploads,
  useOrganizationArtifactEventStream,
  useUploadAdminOrganizationArtifacts,
  useUploadOrganizationArtifacts,
  validateFiles,
} from '@features/patient-artifacts';
import { useCan, useGroupUserRoles, useUserRoles } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import { Alert, Box, Button, LinearProgress } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { normalizeProviderLicenseTypes } from '@utils/providerLicenseTypes';
import { ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import {
  acknowledgePersistedFieldValue,
  durableRequiredCompletion,
  findIntakeItemForField,
  hydrateFieldStates,
  type IntakeFieldStates,
  intakeFieldValuesEqual,
  maskedSensitiveValuesByFieldId,
  replaceFieldValueWithAcceptedSuggestion,
  updateFieldStatesForDraftChange,
} from '../../fieldPersistence';
import { useSerializedIntakeWrites } from '../../hooks/useSerializedIntakeWrites';
import {
  intakeItemAuthoritiesEqual,
  type IntakeItemAuthority,
  type IntakeItemAuthorityEntry,
  reconcileIntakeItems,
  storeMutationItem,
} from '../../itemAuthority';
import {
  hydrateRepeatRecords,
  type IntakeRepeatGroupStates,
  type IntakeRepeatRecord,
  reconcileRepeatGroupStates,
  repeatGroupKey,
  repeatRecordsEqual,
  repeatRecordsEvidence,
} from '../../repeatableFields';
import { intakePagePath, resolveIntakePageKey } from '../../routing';
import {
  useAcceptIntakeHubDocumentFieldSuggestionsMutation,
  useAddAdminIntakeProviderRosterEntryMutation,
  useAddIntakeProviderRosterEntryMutation,
  useDiscardAdminIntakeHubVerificationMutation,
  useGenerateAdminIntakeFormMutation,
  useGenerateIntakeFormMutation,
  useGetAdminIntakeFormConfigurationQuery,
  useGetAdminIntakeHubAggregateQuery,
  useGetAdminIntakeHubDashboardQuery,
  useGetAdminIntakeHubDocumentsQuery,
  useGetIntakeFormConfigurationQuery,
  useGetIntakeHubDashboardQuery,
  useGetIntakeHubDocumentsQuery,
  useMaterializeAdminIntakeHubItemsMutation,
  useMaterializeIntakeHubItemsMutation,
  useRemoveAdminIntakeProviderRosterEntryMutation,
  useRemoveIntakeProviderRosterEntryMutation,
  useUpdateAdminIntakeFormConfigurationMutation,
  useUpdateAdminIntakeHubItemEvidenceMutation,
  useUpdateIntakeFormConfigurationMutation,
  useUpdateIntakeHubItemEvidenceMutation,
  useUpdateIntakeHubSensitiveFieldMutation,
  useVerifyAdminIntakeHubMutation,
} from '../../store/api';
import { SuggestionAcceptanceError } from '../../suggestionAcceptance';
import type {
  GeneratedIntakeField,
  GeneratedIntakeItem,
  IntakeFieldSuggestion,
  IntakeFieldSuggestionAcceptanceRequest,
  IntakeFieldSuggestionAcceptanceResponse,
  IntakeFieldValue,
  IntakeFormConfiguration,
  IntakeFormConfigurationUpdate,
  IntakeHubDocumentInventoryItem,
  IntakePayerRosterEntry,
} from '../../types';
import {
  canDisplayDocumentSuggestion,
  intakeDocumentInventoryItemToDetail,
} from '../intakeDocumentDetail';

import { type AdminSaveKind, formatAdminSaveErrorMessage } from './adminSaveErrors';
import { AggregateIntakePage } from './AggregateIntakePage';
import { IntakeAdminPayerSearch } from './IntakeAdminPayerSearch';
import { IntakeCompletionPage } from './IntakeCompletionPage';
import { type EligibleProvider, type IntakeConfig, IntakeConfigPage } from './IntakeConfigPage';
import {
  type DocumentRequirementStatus,
  type FoundDocument,
  IntakeDocumentsPage,
} from './IntakeDocumentsPage';
import { IntakeFieldsPage } from './IntakeFieldsPage';
import { type ForecastDocument, IntakeForecastPage } from './IntakeForecastPage';
import { IntakeProviderRosterPage } from './IntakeProviderRosterPage';
import { IntakeReviewPage } from './IntakeReviewSubmitPage';
import { IntakeShell, type RailProgressState } from './IntakeShell';
import { RAIL_SECTIONS } from './navigation';
import type { PayerRow } from './types';

const ORDERED_KEYS = RAIL_SECTIONS.flatMap(section => section.items.map(item => item.pageKey));

export type IntakeExperienceContext =
  | { kind: 'provider' }
  | {
      kind: 'admin';
      organizationId: string;
      userId?: string;
      providerEmail?: string;
      targetLabel?: string;
      eligibleProviders?: EligibleProvider[];
    };

interface IntakeExperienceProps {
  context?: IntakeExperienceContext;
  onDirtyChange?: (isDirty: boolean) => void;
}

const PROVIDER_CONTEXT: IntakeExperienceContext = { kind: 'provider' };

/** Documents pages get the drop zone; every other page renders fields. */
const DOCUMENT_PAGES: Record<string, { title: string; sub: string; hint: string }> = {
  M1: {
    title: 'Clinic documents',
    sub: 'Upload your W-9 here so it is included with your intake.',
    hint: "PDF or photo · You'll enter and confirm its details on the next pages",
  },
  P1: {
    title: 'Your documents',
    sub: 'Upload your supporting documents here so they are included with your intake.',
    hint: "PDF or photo · You'll enter and confirm their details on the next pages",
  },
};

function documentDisplayStatus(
  processingStatus?: string | null,
): 'parsing' | 'uploaded' | 'failed' {
  if (processingStatus === 'failed') return 'failed';
  if (processingStatus === 'enriched' || processingStatus === 'processed') return 'uploaded';
  return 'parsing';
}

function persistedDocumentToFoundDocument(
  documentItem: IntakeHubDocumentInventoryItem,
  fields: GeneratedIntakeField[],
): FoundDocument {
  const documentReference = documentItem.document_reference;
  return {
    artifactId: documentReference.source_artifact_id ?? null,
    filename: documentItem.original_filename ?? documentReference.title ?? 'Untitled document',
    status: documentDisplayStatus(documentReference.processing_status),
    docKind: documentReference.doc_kind ?? null,
    filledCount:
      (documentItem.field_suggestions?.length ?? 0) + (documentItem.extracted_fields?.length ?? 0),
    understood: documentReference.processing_status !== 'failed',
    detail: intakeDocumentInventoryItemToDetail(documentItem, fields),
  };
}

function documentFields(fields: GeneratedIntakeField[]): GeneratedIntakeField[] {
  return fields.filter(
    field =>
      field.is_writable !== false &&
      field.data_type.toLowerCase() === 'file' &&
      field.destination.kind === 'document_reference',
  );
}

function requiredDocumentFields(fields: GeneratedIntakeField[]): GeneratedIntakeField[] {
  return documentFields(fields).filter(field => field.is_required);
}

function normalizeDocumentKind(value?: string | null): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function fieldDocumentKinds(field: GeneratedIntakeField): string[] {
  const requirementKind = normalizeDocumentKind(field.requirement_key?.split('.').at(-1));
  const destinationKind =
    field.destination.kind === 'document_reference'
      ? normalizeDocumentKind(field.destination.field_key.replace(/_file$/, ''))
      : '';
  return [requirementKind, destinationKind].filter(Boolean);
}

function documentMatchesFields(
  document: FoundDocument,
  fields: GeneratedIntakeField[],
  items: GeneratedIntakeItem[],
  currentUserId?: string | null,
): boolean {
  const fieldsForCanvas = documentFields(fields);
  const linkedToCanvas = fieldsForCanvas.some(field => {
    const intakeItem = findIntakeItemForField(field, items, currentUserId);
    return (document.detail?.linkedItems ?? []).some(link =>
      intakeItem?.intake_item_id
        ? link.intake_item_id === intakeItem.intake_item_id
        : link.requirement_key === field.requirement_key,
    );
  });
  if (linkedToCanvas) return true;

  const documentKind = normalizeDocumentKind(document.docKind);
  return (
    documentKind.length > 0 &&
    fieldsForCanvas.some(field => fieldDocumentKinds(field).includes(documentKind))
  );
}

/**
 * Per-required-document satisfaction for the "What to upload" list. Groups
 * required document fields onto their backing dashboard item (same join the
 * upload-canvas rail completion uses) and reads satisfaction straight off
 * `is_complete` / `satisfied_by_document_reference_id` rather than
 * re-deriving it with the fuzzy `documentMatchesFields` doc-kind match.
 *
 * When the satisfying document resolves to a `FoundDocument` (via
 * `documentByReferenceId`), it's threaded through so the requirement row can
 * open the exact same preview dialog the "Documents received" list uses,
 * instead of duplicating dialog state.
 */
function documentRequirementStatuses(
  fields: GeneratedIntakeField[],
  items: GeneratedIntakeItem[],
  currentUserId: string | null | undefined,
  documentByReferenceId: Record<string, FoundDocument>,
): DocumentRequirementStatus[] {
  const grouped = new Map<
    string,
    { label: string; item?: GeneratedIntakeItem; groupId?: string }
  >();
  requiredDocumentFields(fields).forEach(field => {
    const item = findIntakeItemForField(field, items, currentUserId);
    const key = item?.intake_item_id ?? `${field.view}:${field.requirement_key ?? field.field_id}`;
    if (!grouped.has(key)) {
      grouped.set(key, { label: field.label, item, groupId: field.group_id });
    }
  });
  return [...grouped.entries()].map(([key, { label, item, groupId }]) => {
    const referenceId = item?.satisfied_by_document_reference_id ?? null;
    const satisfiedDocument = referenceId ? documentByReferenceId[referenceId] : undefined;
    return {
      key,
      label,
      groupId,
      satisfied: Boolean(item?.is_complete),
      satisfiedByFilename: satisfiedDocument?.filename,
      satisfiedDocument,
    };
  });
}

function pageLabel(pageKey: string): string {
  return (
    RAIL_SECTIONS.flatMap(section => section.items).find(item => item.pageKey === pageKey)?.label ??
    pageKey
  );
}

function defaultIntakeConfig(): IntakeConfig {
  return {
    role: 'manager_who_also_practices',
    products: [],
    isPrescriber: true,
    licenseTypes: ['PMHNP'],
    practiceType: 'group',
    completedPages: [],
  };
}

function serverConfigurationToIntakeConfig(configuration: IntakeFormConfiguration): IntakeConfig {
  return {
    role: configuration.role,
    products: configuration.products ?? [],
    isPrescriber: configuration.is_prescriber ?? false,
    licenseTypes: normalizeProviderLicenseTypes(configuration.license_types ?? []) ?? [],
    practiceType: configuration.practice_type ?? 'group',
    completedPages: configuration.completed_pages ?? [],
  };
}

function intakeConfigurationUpdate(config: IntakeConfig): IntakeFormConfigurationUpdate {
  return {
    role: config.role,
    practice_type: config.practiceType,
    is_prescriber: config.isPrescriber,
    license_types: config.licenseTypes,
    completed_pages: config.completedPages,
  };
}

function configurationsMatch(left: IntakeConfig, right: IntakeConfig): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function suggestionIdentity(suggestion: IntakeFieldSuggestion): string {
  return [
    suggestion.target_intake_item_id,
    suggestion.extraction_id,
    suggestion.field_id,
    suggestion.repeat_record_id ?? '',
  ].join(':');
}

function acceptanceRequestIdentity(request: IntakeFieldSuggestionAcceptanceRequest): string {
  return JSON.stringify([
    request.intake_item_id,
    request.document_reference_id,
    request.extraction_id,
    request.field_id,
    request.repeat_record_id ?? '',
  ]);
}

function acceptanceOperationIdentity(requests: IntakeFieldSuggestionAcceptanceRequest[]): string {
  return JSON.stringify(requests.map(acceptanceRequestIdentity).sort());
}

function hasExactlyCorrelatedAcceptanceResults(
  requests: IntakeFieldSuggestionAcceptanceRequest[],
  response: unknown,
): response is IntakeFieldSuggestionAcceptanceResponse {
  if (!response || typeof response !== 'object') return false;
  const results = (response as { results?: unknown }).results;
  if (!Array.isArray(results) || results.length !== requests.length) return false;

  return results.every((result: unknown, index) => {
    if (!result || typeof result !== 'object') return false;
    const record = result as Record<string, unknown>;
    const request = requests[index];
    return (
      record.intake_item_id === request.intake_item_id &&
      record.document_reference_id === request.document_reference_id &&
      record.extraction_id === request.extraction_id &&
      record.field_id === request.field_id &&
      (record.repeat_record_id ?? null) === (request.repeat_record_id ?? null) &&
      (record.outcome === 'accepted' ||
        record.outcome === 'conflict' ||
        record.outcome === 'not_found')
    );
  });
}

export function IntakeExperience({
  context = PROVIDER_CONTEXT,
  onDirtyChange,
}: IntakeExperienceProps) {
  const isAdmin = context.kind === 'admin';
  const userRoles = useUserRoles();
  const groupUserRoles = useGroupUserRoles();
  const canVerifyIntake = isAdmin && userRoles.includes('Admin');
  // Intake scope follows authenticated membership, never the editable form role.
  const providerView =
    userRoles.includes(ROLES.PRACTICE_ADMINISTRATOR) ||
    groupUserRoles.includes(ROLES.PRACTICE_ADMINISTRATOR)
      ? 'all'
      : 'individual';
  // Only the staff surface without a selected user is truly aggregate/read-only.
  // Practice managers request the all-scope dashboard for visibility, but the
  // backend preserves their authenticated user_id so their own clinic intake
  // remains writable.
  const isAggregateView = context.kind === 'admin' && !context.userId;
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { intakeHub } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const getDocumentUrl = useArtifactDownload();
  const [config, setConfig] = useState<IntakeConfig>(defaultIntakeConfig);
  const [acknowledgedConfig, setAcknowledgedConfig] = useState<IntakeConfig>(defaultIntakeConfig);
  const [hydratedConfigurationScope, setHydratedConfigurationScope] = useState<string | null>(null);
  const adminStep = new URLSearchParams(location.search).get('intakeStep');
  const routedPageKey = isAdmin
    ? resolveIntakePageKey(`/intake/${adminStep ?? ''}`)
    : resolveIntakePageKey(location.pathname);
  const currentKey = routedPageKey ?? 'K1';
  const [gateOpenedInSession, setGateOpenedInSession] = useState(false);
  const [fieldStates, setFieldStates] = useState<IntakeFieldStates>({});
  const [pendingSensitiveConfirmations, setPendingSensitiveConfirmations] = useState<Set<string>>(
    () => new Set(),
  );
  const [discardedSuggestionIds, setDiscardedSuggestionIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isSavingAdminChanges, setIsSavingAdminChanges] = useState(false);
  const [isSavingPageCompletion, setIsSavingPageCompletion] = useState(false);
  const [adminSaveErrorCount, setAdminSaveErrorCount] = useState(0);
  const [adminSaveErrorMessage, setAdminSaveErrorMessage] = useState<string>();
  const [completionErrorPageKey, setCompletionErrorPageKey] = useState<
    IntakeConfig['completedPages'][number] | null
  >(null);
  const [docKinds, setDocKinds] = useState<
    Record<string, { docKind: string | null; filled: number }>
  >({});
  const [aggregatePage, setAggregatePage] = useState(1);

  const configurationScope =
    context.kind === 'admin'
      ? `${context.organizationId}:${context.userId ?? 'all-users'}`
      : 'provider';
  const itemAuthorityScope =
    context.kind === 'admin' ? configurationScope : `provider:${providerView}`;
  const [itemAuthorityState, setItemAuthorityState] = useState<{
    authority: IntakeItemAuthority;
    scope: string;
  }>(() => ({ authority: new Map(), scope: itemAuthorityScope }));
  const scopedItemAuthority = useMemo<IntakeItemAuthority>(
    () =>
      itemAuthorityState.scope === itemAuthorityScope
        ? itemAuthorityState.authority
        : new Map<string, IntakeItemAuthorityEntry>(),
    [itemAuthorityScope, itemAuthorityState],
  );
  const {
    currentData: providerConfiguration,
    error: providerConfigurationError,
    isFetching: isProviderConfigurationFetching,
    refetch: refetchProviderConfiguration,
  } = useGetIntakeFormConfigurationQuery(undefined, { skip: isAdmin });
  const {
    currentData: adminConfiguration,
    error: adminConfigurationError,
    isFetching: isAdminConfigurationFetching,
    refetch: refetchAdminConfiguration,
  } = useGetAdminIntakeFormConfigurationQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      userId: context.kind === 'admin' ? (context.userId ?? '') : '',
    },
    { skip: !isAdmin || context.kind !== 'admin' || !context.userId },
  );
  const serverConfiguration = isAdmin ? adminConfiguration : providerConfiguration;
  const gateOpen =
    isAggregateView || gateOpenedInSession || Boolean(serverConfiguration?.last_change);
  const configurationError = isAdmin ? adminConfigurationError : providerConfigurationError;
  const isConfigurationFetching = isAdmin
    ? isAdminConfigurationFetching
    : isProviderConfigurationFetching;
  const refetchConfiguration = isAdmin ? refetchAdminConfiguration : refetchProviderConfiguration;
  const {
    currentData: providerDashboard,
    error: providerDashboardError,
    isFetching: isProviderDashboardFetching,
    refetch: refetchProviderDashboard,
  } = useGetIntakeHubDashboardQuery({ experience: 'v2', view: providerView }, { skip: isAdmin });
  const {
    currentData: adminDashboard,
    error: adminDashboardError,
    isFetching: isAdminDashboardFetching,
    refetch: refetchAdminDashboard,
  } = useGetAdminIntakeHubDashboardQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      userId: context.kind === 'admin' ? context.userId : undefined,
      experience: 'v2',
    },
    { skip: !isAdmin },
  );
  const rawDashboard = isAdmin ? adminDashboard : providerDashboard;
  const reconciledDashboardItems = useMemo(
    () => reconcileIntakeItems(rawDashboard?.items ?? [], scopedItemAuthority),
    [rawDashboard?.items, scopedItemAuthority],
  );
  useEffect(() => {
    setItemAuthorityState(previous => {
      const previousAuthority =
        previous.scope === itemAuthorityScope
          ? previous.authority
          : new Map<string, IntakeItemAuthorityEntry>();
      if (intakeItemAuthoritiesEqual(previousAuthority, reconciledDashboardItems.authority)) {
        return previous.scope === itemAuthorityScope
          ? previous
          : { authority: previousAuthority, scope: itemAuthorityScope };
      }
      return { authority: reconciledDashboardItems.authority, scope: itemAuthorityScope };
    });
  }, [itemAuthorityScope, reconciledDashboardItems.authority]);
  const dashboard = useMemo(
    () =>
      rawDashboard ? { ...rawDashboard, items: reconciledDashboardItems.items } : rawDashboard,
    [rawDashboard, reconciledDashboardItems.items],
  );
  // journey_status.submitted_at is server truth; the client-side completion and
  // gate computations can disagree with it (a newly required field, an
  // unacknowledged Availity field in admin hydration, a hydration race). The
  // admin verify and discard controls live only on X1, so an admin looking at a
  // submitted intake must be able to reach X1 even then — otherwise
  // verification has no reachable surface at all.
  const adminVerificationNeedsCompletionPage =
    context.kind === 'admin' && Boolean(dashboard?.journey_status?.submitted_at);
  const dashboardError = isAdmin ? adminDashboardError : providerDashboardError;
  const isDashboardFetching = isAdmin ? isAdminDashboardFetching : isProviderDashboardFetching;
  const refetchDashboard = isAdmin ? refetchAdminDashboard : refetchProviderDashboard;
  const {
    currentData: aggregateView,
    error: aggregateViewError,
    isFetching: isAggregateViewFetching,
    refetch: refetchAggregateView,
  } = useGetAdminIntakeHubAggregateQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      page: aggregatePage,
      perPage: 20,
    },
    { skip: !isAggregateView },
  );
  const payerRosterItem = useMemo(
    () =>
      (dashboard?.items ?? []).find(
        item => item.requirement_key === 'jotbill.payer_roster' && item.user_id == null,
      ),
    [dashboard?.items],
  );
  const persistedPayerRoster = useMemo(
    () => payerRosterItem?.evidence_summary?.payer_roster ?? [],
    [payerRosterItem?.evidence_summary?.payer_roster],
  );
  const normalizedPersistedPayerRoster = useMemo(
    () =>
      persistedPayerRoster.map(payer => ({
        ...payer,
        enrollment_status: payer.enrollment_status ?? 'already_enrolled',
        jotbill: payer.jotbill ?? false,
        jotcred: payer.jotcred ?? false,
      })),
    [persistedPayerRoster],
  );
  const [payerRosterDraft, setPayerRosterDraft] = useState<IntakePayerRosterEntry[]>([]);
  const [acknowledgedPayerRoster, setAcknowledgedPayerRoster] = useState<IntakePayerRosterEntry[]>(
    [],
  );
  const payerRosterDirty =
    JSON.stringify(payerRosterDraft) !== JSON.stringify(acknowledgedPayerRoster);
  const hydratedPayerRosterItemId = useRef<string | null>(null);

  useEffect(() => {
    const itemId = payerRosterItem?.intake_item_id ?? null;
    if (hydratedPayerRosterItemId.current !== itemId) {
      hydratedPayerRosterItemId.current = itemId;
      setPayerRosterDraft(normalizedPersistedPayerRoster);
      setAcknowledgedPayerRoster(normalizedPersistedPayerRoster);
      return;
    }
    if (!payerRosterDirty) {
      setPayerRosterDraft(normalizedPersistedPayerRoster);
      setAcknowledgedPayerRoster(normalizedPersistedPayerRoster);
    }
  }, [normalizedPersistedPayerRoster, payerRosterDirty, payerRosterItem?.intake_item_id]);
  const { data: providerDocumentInventory, refetch: refetchProviderDocumentInventory } =
    useGetIntakeHubDocumentsQuery({ experience: 'v2' }, { skip: isAdmin || !dashboard });
  const { data: adminDocumentInventory, refetch: refetchAdminDocumentInventory } =
    useGetAdminIntakeHubDocumentsQuery(
      {
        organizationId: context.kind === 'admin' ? context.organizationId : '',
        userId: context.kind === 'admin' ? context.userId : undefined,
      },
      { skip: !isAdmin || !dashboard || isAggregateView },
    );
  const documentInventory = isAdmin ? adminDocumentInventory : providerDocumentInventory;
  const refetchDocumentInventory = isAdmin
    ? refetchAdminDocumentInventory
    : refetchProviderDocumentInventory;
  const [generateProviderForm, { data: providerForm }] = useGenerateIntakeFormMutation();
  const [generateAdminForm, { data: adminForm }] = useGenerateAdminIntakeFormMutation();
  const [materializeProviderItems] = useMaterializeIntakeHubItemsMutation();
  const [materializeAdminItems] = useMaterializeAdminIntakeHubItemsMutation();
  const form = isAdmin ? adminForm : providerForm;
  const [updateProviderEvidence] = useUpdateIntakeHubItemEvidenceMutation();
  const [updateAdminEvidence] = useUpdateAdminIntakeHubItemEvidenceMutation();
  const [updateProviderConfiguration] = useUpdateIntakeFormConfigurationMutation();
  const [updateAdminConfiguration] = useUpdateAdminIntakeFormConfigurationMutation();
  const [updateSensitiveField] = useUpdateIntakeHubSensitiveFieldMutation();
  const [verifyAdminIntake, { isLoading: isVerifyingAdminIntake }] =
    useVerifyAdminIntakeHubMutation();
  const [discardAdminIntakeVerification, { isLoading: isDiscardingAdminVerification }] =
    useDiscardAdminIntakeHubVerificationMutation();
  const [acceptDocumentFieldSuggestions] = useAcceptIntakeHubDocumentFieldSuggestionsMutation();
  const storeAcceptedItems = useCallback(
    (items: GeneratedIntakeItem[]) => {
      setItemAuthorityState(previous => ({
        authority: items.reduce(
          (authority, item) => storeMutationItem(authority, item),
          previous.scope === itemAuthorityScope
            ? previous.authority
            : new Map<string, IntakeItemAuthorityEntry>(),
        ),
        scope: itemAuthorityScope,
      }));
    },
    [itemAuthorityScope],
  );
  const storeAcceptedItem = useCallback(
    (item: GeneratedIntakeItem) => storeAcceptedItems([item]),
    [storeAcceptedItems],
  );
  const writeQueue = useSerializedIntakeWrites();
  const acceptanceOperationTail = useRef<Promise<void>>(Promise.resolve());
  const acceptanceOperations = useRef(new Map<string, Promise<void>>());
  const acceptedSuggestionRequestIds = useRef(new Set<string>());
  const lastFormGenerationSignature = useRef<string | null>(null);
  const inFlightFormGenerationSignatures = useRef(new Set<string>());
  const automaticallyRetriedFormGenerationSignatures = useRef(new Set<string>());
  const currentFormGenerationSignature = useRef<string | null>(null);
  const [formGenerationRetrySequence, setFormGenerationRetrySequence] = useState(0);
  const materializationScope = itemAuthorityScope;
  const [materializationState, setMaterializationState] = useState<{
    scope: string;
    status: 'idle' | 'pending' | 'succeeded' | 'failed';
  }>(() => ({ scope: materializationScope, status: 'idle' }));
  const materializationStatus =
    materializationState.scope === materializationScope ? materializationState.status : 'idle';
  const currentMaterializationScope = useRef(materializationScope);
  const currentMaterializationAttempt = useRef<object | null>(null);
  if (currentMaterializationScope.current !== materializationScope) {
    currentMaterializationScope.current = materializationScope;
    currentMaterializationAttempt.current = null;
  }

  const runMaterialization = useCallback(async () => {
    if (isAggregateView || currentMaterializationAttempt.current) return;
    const attempt = {};
    currentMaterializationAttempt.current = attempt;
    const isCurrentAttempt = () =>
      currentMaterializationScope.current === materializationScope &&
      currentMaterializationAttempt.current === attempt;
    setMaterializationState({ scope: materializationScope, status: 'pending' });
    try {
      const response =
        context.kind === 'admin'
          ? await materializeAdminItems({
              organizationId: context.organizationId,
              userId: context.userId ?? '',
            }).unwrap()
          : await materializeProviderItems().unwrap();
      if (!isCurrentAttempt()) return;
      storeAcceptedItems(response.items ?? []);
      await Promise.all([refetchDashboard(), refetchDocumentInventory()]);
      if (!isCurrentAttempt()) return;
      setMaterializationState(previous =>
        previous.scope === materializationScope
          ? { scope: materializationScope, status: 'succeeded' }
          : previous,
      );
    } catch {
      if (!isCurrentAttempt()) return;
      setMaterializationState(previous =>
        previous.scope === materializationScope
          ? { scope: materializationScope, status: 'failed' }
          : previous,
      );
    } finally {
      if (currentMaterializationAttempt.current === attempt) {
        currentMaterializationAttempt.current = null;
      }
    }
  }, [
    context,
    isAggregateView,
    materializationScope,
    materializeAdminItems,
    materializeProviderItems,
    refetchDashboard,
    refetchDocumentInventory,
    storeAcceptedItems,
  ]);

  useEffect(() => {
    if (!dashboard || materializationStatus !== 'idle' || isAggregateView) return;
    void runMaterialization();
  }, [dashboard, isAggregateView, materializationStatus, runMaterialization]);
  const {
    triggerAutoSave: triggerProviderConfigurationSave,
    saveImmediatelyAndWait: saveProviderConfigurationImmediately,
    status: providerConfigurationSaveStatus,
    lastSavedAt: providerConfigurationLastSavedAt,
    hasPendingSave: hasPendingProviderConfigurationSave,
  } = useAutoSave<IntakeConfig>({
    enabled: !isAdmin && !isAggregateView && hydratedConfigurationScope === configurationScope,
    debounceMs: 800,
    flushOnUnmount: true,
    onSave: async nextConfiguration => {
      await updateProviderConfiguration({
        intakeFormConfigurationProviderUpdateRequest: intakeConfigurationUpdate(nextConfiguration),
      }).unwrap();
      setAcknowledgedConfig(nextConfiguration);
    },
  });
  // Mirrors the `enabled` condition above: while a provider's configuration hasn't
  // hydrated yet, saveProviderConfigurationImmediately (saveImmediatelyAndWait) rejects
  // instead of saving. A page that completes via that save (currently just F1) must
  // disable its Continue button during this window rather than let the click through
  // and surface a save-failure error for what's really just a loading state.
  const isProviderConfigurationHydrating =
    !isAdmin && hydratedConfigurationScope !== configurationScope;

  const organizationId = dashboard?.clinician?.organization_id;
  const userId = dashboard?.clinician?.user_id;
  const assignedProducts = useMemo<IntakeConfig['products']>(() => {
    const products = new Set<IntakeConfig['products'][number]>();
    const orderedProducts: IntakeConfig['products'] = ['jotcred', 'jotbill'];
    for (const enrollment of dashboard?.enrollments ?? []) {
      if (enrollment.service_key === 'jotcred' || enrollment.service_key === 'jotbill') {
        products.add(enrollment.service_key);
      }
    }
    return orderedProducts.filter(product => products.has(product));
  }, [dashboard?.enrollments]);
  const effectiveConfig = useMemo<IntakeConfig>(
    () => ({ ...config, products: assignedProducts }),
    [assignedProducts, config],
  );
  const shouldLoadEligibleProviders =
    effectiveConfig.practiceType === 'group' && effectiveConfig.role !== 'provider';
  // The clinician-role member list is only the candidate pool for the picker.
  // The roster itself is the explicit list stored in the Intake Hub tables.
  const {
    clinicians: providerRosterCandidatePool,
    isLoading: providerCandidatesLoading,
    error: providerCandidatesQueryError,
    refetch: refetchProviderCandidates,
  } = useClinicians({ skip: isAdmin || !shouldLoadEligibleProviders });
  const contextEligibleProviders = context.kind === 'admin' ? context.eligibleProviders : undefined;
  const rosterCandidatePool = useMemo(
    () => (isAdmin ? (contextEligibleProviders ?? []) : providerRosterCandidatePool),
    [contextEligibleProviders, isAdmin, providerRosterCandidatePool],
  );
  const providerRosterEntries = useMemo(
    () => dashboard?.provider_roster ?? [],
    [dashboard?.provider_roster],
  );
  const eligibleProviders = useMemo<EligibleProvider[]>(() => {
    const namesByUserId = new Map(
      rosterCandidatePool.map(candidate => [candidate.userId, candidate.name]),
    );
    return providerRosterEntries.map(entry => ({
      userId: entry.user_id,
      name: namesByUserId.get(entry.user_id) ?? entry.user_id,
      percentComplete: entry.progress?.percent_complete,
      status: entry.status ?? undefined,
      // journey_status is served by the roster payload; read it defensively so a
      // not-yet-regenerated API client still compiles.
      verifiedAt:
        (entry as { journey_status?: { verified_at?: string | null } }).journey_status
          ?.verified_at ?? null,
    }));
  }, [providerRosterEntries, rosterCandidatePool]);
  const rosterCandidates = useMemo(() => {
    const rosteredUserIds = new Set(providerRosterEntries.map(entry => entry.user_id));
    return rosterCandidatePool.filter(candidate => !rosteredUserIds.has(candidate.userId));
  }, [providerRosterEntries, rosterCandidatePool]);
  const eligibleProvidersLoading =
    context.kind === 'admin'
      ? false
      : providerCandidatesLoading || (isDashboardFetching && !dashboard);
  const eligibleProvidersQueryError =
    context.kind === 'admin' ? undefined : providerCandidatesQueryError;
  // Practice administrators manage their own org's intake roster from the
  // provider-facing wizard, not just JotPsych staff via the admin embed.
  // `manage_members` is granted to exactly the practice_administrator role
  // (ADR 0073 catalog) and is already the canonical "isPracticeAdmin" check
  // used elsewhere (e.g. CliniciansPage, useAddonUpsell).
  const canManageRosterAsPracticeAdmin = useCan('manage_members');
  const canManageRoster = isAdmin || canManageRosterAsPracticeAdmin;
  const [addProviderRosterEntry, addProviderRosterEntryResult] =
    useAddIntakeProviderRosterEntryMutation();
  const [removeProviderRosterEntry] = useRemoveIntakeProviderRosterEntryMutation();
  const [addAdminProviderRosterEntry, addAdminProviderRosterEntryResult] =
    useAddAdminIntakeProviderRosterEntryMutation();
  const [removeAdminProviderRosterEntry] = useRemoveAdminIntakeProviderRosterEntryMutation();
  const isAddingRosterProvider =
    addProviderRosterEntryResult.isLoading || addAdminProviderRosterEntryResult.isLoading;
  const handleAddRosterProvider = useCallback(
    (rosterUserId: string) => {
      if (context.kind === 'admin') {
        void addAdminProviderRosterEntry({
          organizationId: context.organizationId,
          intakeHubProviderRosterAddRequest: { user_id: rosterUserId },
        });
        return;
      }
      void addProviderRosterEntry({
        intakeHubProviderRosterAddRequest: { user_id: rosterUserId },
      });
    },
    [addAdminProviderRosterEntry, addProviderRosterEntry, context],
  );
  const handleRemoveRosterProvider = useCallback(
    (rosterUserId: string) => {
      if (context.kind === 'admin') {
        void removeAdminProviderRosterEntry({
          organizationId: context.organizationId,
          userId: rosterUserId,
        });
        return;
      }
      void removeProviderRosterEntry({ userId: rosterUserId });
    },
    [context, removeAdminProviderRosterEntry, removeProviderRosterEntry],
  );
  const hasUnsavedAdminChanges =
    isAdmin &&
    (!configurationsMatch(config, acknowledgedConfig) ||
      payerRosterDirty ||
      Object.values(fieldStates).some(
        state => JSON.stringify(state.draftValue) !== JSON.stringify(state.acknowledgedValue),
      ));

  const handleVerifyAdminIntake = useCallback(async () => {
    if (
      context.kind !== 'admin' ||
      !context.userId ||
      !canVerifyIntake ||
      hasUnsavedAdminChanges ||
      isSavingAdminChanges ||
      adminSaveErrorCount > 0
    )
      return;
    try {
      await verifyAdminIntake({
        organizationId: context.organizationId,
        userId: context.userId,
      }).unwrap();
      enqueueSnackbar('Intake verified.', { variant: 'success' });
    } catch {
      enqueueSnackbar('We could not verify this intake. Refresh and try again.', {
        variant: 'error',
      });
    }
  }, [
    adminSaveErrorCount,
    canVerifyIntake,
    context,
    enqueueSnackbar,
    hasUnsavedAdminChanges,
    isSavingAdminChanges,
    verifyAdminIntake,
  ]);

  const handleDiscardAdminVerification = useCallback(async () => {
    if (context.kind !== 'admin' || !context.userId || !canVerifyIntake) return;
    try {
      await discardAdminIntakeVerification({
        organizationId: context.organizationId,
        userId: context.userId,
      }).unwrap();
      enqueueSnackbar('Verification discarded.', { variant: 'success' });
    } catch {
      enqueueSnackbar('We could not discard this verification. Refresh and try again.', {
        variant: 'error',
      });
    }
  }, [canVerifyIntake, context, discardAdminIntakeVerification, enqueueSnackbar]);

  const discardAdminDrafts = useCallback(() => {
    setFieldStates(previous =>
      Object.fromEntries(
        Object.entries(previous).map(([fieldId, state]) => [
          fieldId,
          { ...state, draftValue: state.acknowledgedValue },
        ]),
      ),
    );
    setConfig(acknowledgedConfig);
    setPayerRosterDraft(acknowledgedPayerRoster);
    setAdminSaveErrorCount(0);
    setAdminSaveErrorMessage(undefined);
    setCompletionErrorPageKey(null);
  }, [acknowledgedConfig, acknowledgedPayerRoster]);

  const navigateToPage = useCallback(
    (pageKey: string, replace = false) => {
      if (isAdmin) {
        if (
          hasUnsavedAdminChanges &&
          !window.confirm('Discard unsaved Intake Hub changes and continue?')
        ) {
          return;
        }
        if (hasUnsavedAdminChanges) discardAdminDrafts();
        const search = new URLSearchParams(location.search);
        const slug = intakePagePath(pageKey).split('/').at(-1) ?? 'key-questions';
        search.set('intakeStep', slug);
        navigate({ pathname: location.pathname, search: search.toString() }, { replace });
        return;
      }
      navigate(
        {
          pathname: intakePagePath(pageKey),
          search: location.search,
        },
        { replace },
      );
    },
    [
      discardAdminDrafts,
      hasUnsavedAdminChanges,
      isAdmin,
      location.pathname,
      location.search,
      navigate,
    ],
  );

  useEffect(() => {
    onDirtyChange?.(hasUnsavedAdminChanges);
  }, [hasUnsavedAdminChanges, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  useEffect(() => {
    if (!hasUnsavedAdminChanges) return undefined;
    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [hasUnsavedAdminChanges]);

  useEffect(() => {
    if (!routedPageKey) navigateToPage('K1', true);
  }, [navigateToPage, routedPageKey]);

  useEffect(() => {
    const defaults = defaultIntakeConfig();
    setConfig(defaults);
    setAcknowledgedConfig(defaults);
    setHydratedConfigurationScope(isAggregateView ? configurationScope : null);
    setAggregatePage(1);
  }, [configurationScope, isAggregateView]);

  useEffect(() => {
    if (!serverConfiguration) return;
    const hydrated = serverConfigurationToIntakeConfig(serverConfiguration);
    setConfig(previous => (configurationsMatch(previous, hydrated) ? previous : hydrated));
    setAcknowledgedConfig(previous =>
      configurationsMatch(previous, hydrated) ? previous : hydrated,
    );
    setHydratedConfigurationScope(configurationScope);
  }, [configurationScope, serverConfiguration]);

  useEffect(() => {
    if (
      assignedProducts.length === 0 ||
      hydratedConfigurationScope !== configurationScope ||
      configurationError
    )
      return;
    const generateIntakeFormRequest = {
      role: effectiveConfig.role,
      products: effectiveConfig.products,
      practice_type: effectiveConfig.practiceType,
      is_prescriber: effectiveConfig.isPrescriber,
      license_types: effectiveConfig.licenseTypes,
      payers: payerRosterDraft.map(payer => ({
        display_name: payer.payer_name,
        primary_payer_id: payer.payer_id,
        states: [],
        plan_types: [],
      })),
    };
    const generationSignature = JSON.stringify({
      configurationScope,
      generateIntakeFormRequest,
    });
    currentFormGenerationSignature.current = generationSignature;
    if (
      generationSignature === lastFormGenerationSignature.current ||
      inFlightFormGenerationSignatures.current.has(generationSignature)
    )
      return;
    inFlightFormGenerationSignatures.current.add(generationSignature);
    let generationRequest;
    if (context.kind === 'admin') {
      if (!context.userId) {
        inFlightFormGenerationSignatures.current.delete(generationSignature);
        return;
      }
      generationRequest = generateAdminForm({
        organizationId: context.organizationId,
        userId: context.userId,
        generateIntakeFormRequest,
      });
    } else {
      generationRequest = generateProviderForm({ generateIntakeFormRequest });
    }
    let generationFailed = false;
    void generationRequest
      .unwrap()
      .then(() => {
        if (currentFormGenerationSignature.current === generationSignature) {
          lastFormGenerationSignature.current = generationSignature;
        }
        automaticallyRetriedFormGenerationSignatures.current.delete(generationSignature);
      })
      .catch(() => {
        generationFailed = true;
      })
      .finally(() => {
        inFlightFormGenerationSignatures.current.delete(generationSignature);
        if (
          generationFailed &&
          currentFormGenerationSignature.current === generationSignature &&
          !automaticallyRetriedFormGenerationSignatures.current.has(generationSignature)
        ) {
          automaticallyRetriedFormGenerationSignatures.current.add(generationSignature);
          setFormGenerationRetrySequence(sequence => sequence + 1);
        }
      });
  }, [
    assignedProducts.length,
    configurationError,
    configurationScope,
    context,
    effectiveConfig,
    formGenerationRetrySequence,
    generateAdminForm,
    generateProviderForm,
    hydratedConfigurationScope,
    payerRosterDraft,
  ]);

  const allFields = useMemo(
    () =>
      (form?.canvases ?? [])
        .filter(canvas => canvas.is_active)
        .flatMap(canvas => canvas.fields ?? []),
    [form],
  );
  // Availity access (I23) renders on K1 alongside the payer list; its dirty/valid
  // state folds into K1's own progress rather than a dedicated review row (M4 is gone).
  const availityField = useMemo(
    () => allFields.find(field => field.group_id === 'I23'),
    [allFields],
  );
  const availityFieldState = availityField ? fieldStates[availityField.field_id] : undefined;
  const availityDirty = Boolean(
    availityFieldState && availityFieldState.draftValue !== availityFieldState.acknowledgedValue,
  );
  const availityValid = !availityField?.is_required || availityFieldState?.draftValue === true;
  const hasMissingMaterializedDestinations = useMemo(
    () =>
      !isAggregateView &&
      allFields.some(field => {
        if (field.is_writable === false) return false;
        if (
          field.destination.kind !== 'intake_item_evidence' &&
          field.destination.kind !== 'intake_item_sensitive'
        ) {
          return false;
        }
        return (
          !field.requirement_key ||
          !findIntakeItemForField(field, dashboard?.items ?? [], userId)?.intake_item_id
        );
      }),
    [allFields, dashboard?.items, isAggregateView, userId],
  );
  const isPreparingMissingItems =
    materializationStatus === 'pending' && hasMissingMaterializedDestinations;
  const fieldPageIndex = useMemo(() => {
    const index: Record<string, string> = {};
    for (const canvas of form?.canvases ?? []) {
      if (!canvas.is_active || !canvas.canvas_key) continue;
      for (const field of canvas.fields ?? []) index[field.field_id] = canvas.canvas_key;
    }
    return index;
  }, [form]);
  const serverRepeatRecordsByGroup = useMemo(() => {
    const grouped = new Map<string, GeneratedIntakeField[]>();
    for (const field of allFields) {
      if (!field.repeat_scope || field.repeat_scope === 'none' || !field.requirement_key) continue;
      const key = repeatGroupKey(field);
      grouped.set(key, [...(grouped.get(key) ?? []), field]);
    }
    return Object.fromEntries(
      [...grouped.entries()].map(([key, fields]) => {
        const item = findIntakeItemForField(fields[0], dashboard?.items ?? [], userId);
        const extracted = item?.evidence_summary?.extracted_fields;
        return [
          key,
          hydrateRepeatRecords(
            extracted && typeof extracted === 'object' ? extracted : {},
            fields.map(field => field.destination.field_key),
            fields[0].requirement_key ?? fields[0].group_id,
          ),
        ];
      }),
    );
  }, [allFields, dashboard?.items, userId]);
  const [repeatGroupStates, setRepeatGroupStates] = useState<IntakeRepeatGroupStates>({});
  useEffect(() => {
    setRepeatGroupStates(previous =>
      reconcileRepeatGroupStates(serverRepeatRecordsByGroup, previous),
    );
  }, [serverRepeatRecordsByGroup]);
  const repeatRecordsByGroup = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(serverRepeatRecordsByGroup).map(([groupKey, serverRecords]) => [
          groupKey,
          repeatGroupStates[groupKey]?.draftRecords ?? serverRecords,
        ]),
      ),
    [repeatGroupStates, serverRepeatRecordsByGroup],
  );
  const acknowledgedRepeatRecordsByGroup = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(serverRepeatRecordsByGroup).map(([groupKey, serverRecords]) => [
          groupKey,
          repeatGroupStates[groupKey]?.acknowledgedRecords ?? serverRecords,
        ]),
      ),
    [repeatGroupStates, serverRepeatRecordsByGroup],
  );
  const activeSuggestionFieldsById = useMemo(
    () =>
      Object.fromEntries(
        allFields.filter(canDisplayDocumentSuggestion).map(field => [field.field_id, field]),
      ),
    [allFields],
  );
  const activeSuggestionFieldIds = useMemo(
    () => Object.keys(activeSuggestionFieldsById),
    [activeSuggestionFieldsById],
  );
  const { documentSuggestionReferenceIds, documentSuggestions } = useMemo(() => {
    const indexed = new Map<string, IntakeFieldSuggestion>();
    const referenceIds = new Map<string, string>();
    const conflicts = new Set<string>();
    if (isAdmin) return { documentSuggestionReferenceIds: {}, documentSuggestions: {} };
    for (const documentItem of documentInventory?.documents ?? []) {
      for (const suggestion of documentItem.field_suggestions ?? []) {
        const field = activeSuggestionFieldsById[suggestion.field_id];
        if (!field) continue;
        const fieldState = fieldStates[suggestion.field_id];
        const isRepeated = Boolean(field.repeat_scope && field.repeat_scope !== 'none');
        const repeatedDraftConflict = (() => {
          if (!isRepeated) return false;
          if (!suggestion.repeat_record_id) return true;
          const groupKey = repeatGroupKey(field);
          const draftMatches = (repeatRecordsByGroup[groupKey] ?? []).filter(
            record => record.record_id === suggestion.repeat_record_id,
          );
          const acknowledgedMatches = (acknowledgedRepeatRecordsByGroup[groupKey] ?? []).filter(
            record => record.record_id === suggestion.repeat_record_id,
          );
          if (draftMatches.length !== 1 || acknowledgedMatches.length !== 1) return true;
          const fieldKey = field.destination.field_key;
          const draftValue = draftMatches[0].values[fieldKey];
          const acknowledgedValue = acknowledgedMatches[0].values[fieldKey];
          return (
            JSON.stringify(draftValue) !== JSON.stringify(acknowledgedValue) ||
            JSON.stringify(draftValue) === JSON.stringify(suggestion.value)
          );
        })();
        if (
          conflicts.has(suggestion.field_id) ||
          discardedSuggestionIds.has(suggestionIdentity(suggestion)) ||
          repeatedDraftConflict ||
          (!isRepeated &&
            fieldState &&
            intakeFieldValuesEqual(fieldState.draftValue, suggestion.value))
        )
          continue;
        const existing = indexed.get(suggestion.field_id);
        if (
          existing &&
          (JSON.stringify(existing.value) !== JSON.stringify(suggestion.value) ||
            existing.repeat_record_id !== suggestion.repeat_record_id)
        ) {
          indexed.delete(suggestion.field_id);
          referenceIds.delete(suggestion.field_id);
          conflicts.add(suggestion.field_id);
          continue;
        }
        indexed.set(suggestion.field_id, suggestion);
        referenceIds.set(
          suggestion.field_id,
          documentItem.document_reference.document_reference_id,
        );
      }
    }
    return {
      documentSuggestionReferenceIds: Object.fromEntries(referenceIds),
      documentSuggestions: Object.fromEntries(indexed),
    };
  }, [
    acknowledgedRepeatRecordsByGroup,
    activeSuggestionFieldsById,
    discardedSuggestionIds,
    documentInventory?.documents,
    fieldStates,
    isAdmin,
    repeatRecordsByGroup,
  ]);
  const suggestionsByPage = useMemo(() => {
    const counts: Record<string, { total: number; unconfirmed: number }> = {};
    for (const suggestion of Object.values(documentSuggestions)) {
      const pageKey = fieldPageIndex[suggestion.field_id];
      if (!pageKey) continue;
      const count = counts[pageKey] ?? { total: 0, unconfirmed: 0 };
      count.total += 1;
      count.unconfirmed += 1;
      counts[pageKey] = count;
    }
    return counts;
  }, [documentSuggestions, fieldPageIndex]);
  const landedSummary = useMemo(() => {
    const parts = ORDERED_KEYS.flatMap(pageKey => {
      const count = suggestionsByPage[pageKey]?.unconfirmed ?? 0;
      return count > 0 ? [`${count} field${count === 1 ? '' : 's'} on ${pageLabel(pageKey)}`] : [];
    });
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [suggestionsByPage]);
  const firstPageWithSuggestions = useMemo(
    () => ORDERED_KEYS.find(pageKey => (suggestionsByPage[pageKey]?.unconfirmed ?? 0) > 0) ?? null,
    [suggestionsByPage],
  );
  useEffect(() => {
    const confirmedSensitiveFields = new Set<string>();
    const fieldsToHydrate = allFields.filter(field => {
      if (
        !pendingSensitiveConfirmations.has(field.field_id) ||
        field.destination.kind !== 'intake_item_sensitive'
      ) {
        return true;
      }
      const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
      const isConfirmed = Boolean(
        item?.sensitive_fields_on_file?.includes(field.destination.field_key),
      );
      if (isConfirmed) confirmedSensitiveFields.add(field.field_id);
      return isConfirmed;
    });
    setFieldStates(previous =>
      hydrateFieldStates(fieldsToHydrate, dashboard?.items ?? [], previous, userId),
    );
    if (confirmedSensitiveFields.size > 0) {
      setPendingSensitiveConfirmations(previous => {
        const next = new Set(previous);
        for (const fieldId of confirmedSensitiveFields) next.delete(fieldId);
        return next;
      });
    }
  }, [allFields, dashboard?.items, pendingSensitiveConfirmations, userId]);

  // ---- uploads -----------------------------------------------------------
  const { constraints: providerUploadConstraints } = useArtifactUploadConstraints();
  const { constraints: adminUploadConstraints } = useAdminArtifactUploadConstraints();
  const constraints = isAdmin ? adminUploadConstraints : providerUploadConstraints;
  const { upload: uploadProviderFiles } = useUploadOrganizationArtifacts(organizationId);
  const { upload: uploadAdminFiles } = useUploadAdminOrganizationArtifacts(
    context.kind === 'admin' ? context.organizationId : '',
  );
  const {
    pendingUploads,
    trackUpload,
    dismissByArtifactId,
    reconcile,
    handleArtifactEvent,
    handleDocRefExtraction,
  } = useOptimisticUploads({ retainUntilReconciled: true });
  const [uploadingFileCount, setUploadingFileCount] = useState(0);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!organizationId || isAggregateView) return;
      if (!userId) {
        enqueueSnackbar('Select a provider before uploading intake documents.', {
          variant: 'warning',
        });
        return;
      }
      const { accepted } = validateFiles(files, constraints);
      if (accepted.length === 0) return;
      const upload = isAdmin ? uploadAdminFiles : uploadProviderFiles;
      setUploadingFileCount(accepted.length);
      let outcome: Awaited<ReturnType<typeof upload>>;
      try {
        outcome = await upload(accepted, {
          sourceChannel: isAdmin ? 'intake_hub_admin' : 'intake_hub',
          intakeHub:
            userId && form?.catalog_version
              ? {
                  userId,
                  catalogVersion: form.catalog_version,
                  activeFieldIds: activeSuggestionFieldIds,
                }
              : undefined,
        });
      } catch (error) {
        enqueueSnackbar(formatUploadLimitError(error), { variant: 'error' });
        return;
      } finally {
        setUploadingFileCount(0);
      }
      if (outcome) {
        trackUpload(outcome);
        void refetchDocumentInventory();
      }
    },
    [
      activeSuggestionFieldIds,
      constraints,
      enqueueSnackbar,
      form?.catalog_version,
      isAdmin,
      isAggregateView,
      organizationId,
      refetchDocumentInventory,
      trackUpload,
      uploadAdminFiles,
      uploadProviderFiles,
      userId,
    ],
  );

  const { isDragging } = useChartWideDropZone({
    enabled:
      !isAggregateView &&
      Boolean(organizationId) &&
      (currentKey in DOCUMENT_PAGES || currentKey === 'M6' || currentKey === 'P6'),
    onDrop: files => {
      void handleFiles(files);
    },
  });
  const isUploadInFlight = uploadingFileCount > 0;

  const handleArtifactStreamEvent = useCallback(
    (event: Parameters<typeof handleArtifactEvent>[0]) => {
      handleArtifactEvent(event);
      void refetchDocumentInventory();
    },
    [handleArtifactEvent, refetchDocumentInventory],
  );

  useOrganizationArtifactEventStream(organizationId, {
    enabled: !isAdmin && Boolean(organizationId),
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetchDocumentInventory();
      void refetchDashboard();
      const summary = event.summary ?? {};
      const artifactId = event.artifactId ?? '';
      if (artifactId) {
        setDocKinds(prev => ({
          ...prev,
          [artifactId]: {
            docKind: (summary as { doc_kind?: string }).doc_kind ?? null,
            filled:
              summary.extracted_fields && typeof summary.extracted_fields === 'object'
                ? Object.keys(summary.extracted_fields).length
                : 0,
          },
        }));
      }
    },
  });
  useAdminOrganizationArtifactEventStream(context.kind === 'admin' ? context.organizationId : '', {
    enabled: isAdmin && Boolean(organizationId),
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetchDocumentInventory();
      void refetchDashboard();
    },
  });

  useEffect(() => {
    reconcile(
      (documentInventory?.documents ?? []).map(documentItem => documentItem.document_reference),
    );
  }, [documentInventory?.documents, reconcile]);

  // ---- derived -----------------------------------------------------------
  const pageStats = useMemo(() => {
    const stats: Record<string, { fieldCount: number; requiredCount: number; active: boolean }> =
      {};
    for (const canvas of form?.canvases ?? []) {
      stats[canvas.canvas_key ?? ''] = {
        fieldCount: canvas.fields?.length ?? 0,
        requiredCount: canvas.required_count ?? 0,
        active: canvas.is_active,
      };
    }
    return stats;
  }, [form]);

  const notNeededKeys = useMemo(() => {
    if (isAggregateView) return new Set<string>();
    return form
      ? new Set(
          ORDERED_KEYS.filter(key => !['K1', 'F1', 'X1'].includes(key) && !pageStats[key]?.active),
        )
      : new Set<string>();
  }, [form, isAggregateView, pageStats]);

  useEffect(() => {
    if (isAggregateView) return;
    if (!form || !routedPageKey || ['K1', 'F1', 'X1'].includes(routedPageKey)) return;
    const pageExists = (form.canvases ?? []).some(canvas => canvas.canvas_key === routedPageKey);
    if (!pageExists) navigateToPage('K1', true);
  }, [form, isAggregateView, navigateToPage, routedPageKey]);

  useEffect(() => {
    // F1 stays reachable while the gate is closed, matching IntakeShell's
    // unlock rule and the stale-route guard above.
    if (!serverConfiguration || gateOpen || ['K1', 'F1'].includes(currentKey)) return;
    if (currentKey === 'X1' && adminVerificationNeedsCompletionPage) return;
    navigateToPage('K1', true);
  }, [
    adminVerificationNeedsCompletionPage,
    currentKey,
    gateOpen,
    navigateToPage,
    serverConfiguration,
  ]);

  const payerRows = useMemo(
    () =>
      payerRosterDraft.map(
        (payer): PayerRow => ({
          displayName: payer.payer_name,
          enrollmentStatus: payer.enrollment_status ?? 'already_enrolled',
          jotbill: payer.jotbill ?? false,
          jotcred: payer.jotcred ?? false,
          payerId: payer.payer_id,
          rowId: payer.payer_id,
        }),
      ),
    [payerRosterDraft],
  );

  const derivedSentence = useMemo(() => {
    const flags = form?.derived_flags;
    if (!flags) return null;
    const parts: string[] = [];
    if (flags.has_medicare && flags.has_medicaid)
      parts.push("Medicare and Medicaid detected → we'll ask for a PTAN and a Medicaid ID later.");
    else if (flags.has_medicare) parts.push("Medicare detected → we'll ask for a PTAN later.");
    else if (flags.has_medicaid)
      parts.push("Medicaid detected → we'll ask for a Medicaid ID later.");
    if (flags.payer_requires_references)
      parts.push('A selected payer needs professional references before we can file there.');
    if (flags.payer_requires_portal_login) parts.push('A selected payer needs a portal login.');
    return parts.length > 0 ? parts.join(' ') : null;
  }, [form]);

  const totalRequired = useMemo(
    () =>
      (form?.canvases ?? [])
        .filter(canvas => canvas.is_active)
        .reduce((sum, canvas) => sum + (canvas.required_count ?? 0), 0),
    [form],
  );

  const documentsToGather: ForecastDocument[] = useMemo(() => {
    const activeDocumentFields = (form?.canvases ?? [])
      .filter(
        canvas =>
          canvas.is_active &&
          canvas.canvas_type === 'upload' &&
          ['clinic_setup', 'your_intake'].includes(canvas.section),
      )
      .flatMap(canvas => canvas.fields ?? [])
      .filter(
        field => field.data_type === 'file' && field.destination.kind === 'document_reference',
      );
    const grouped = new Map<
      string,
      ForecastDocument & { forecastOrder: number; sourceIndex: number }
    >();
    activeDocumentFields.forEach((field, sourceIndex) => {
      const presentation = field.document_forecast;
      const groupKey = presentation?.group_key ?? `field:${field.field_id}`;
      if (grouped.has(groupKey)) return;
      grouped.set(groupKey, {
        fieldId: groupKey,
        label: presentation?.label ?? field.label,
        detail: presentation?.detail ?? (field.help_text || field.condition || undefined),
        forecastOrder: presentation?.order ?? Number.MAX_SAFE_INTEGER,
        sourceIndex,
      });
    });
    return [...grouped.values()]
      .sort(
        (left, right) =>
          left.forecastOrder - right.forecastOrder || left.sourceIndex - right.sourceIndex,
      )
      .map(({ fieldId, label, detail }) => ({ fieldId, label, detail }));
  }, [form]);

  const percentComplete = useMemo(
    () =>
      durableRequiredCompletion(
        allFields,
        fieldStates,
        dashboard?.items ?? [],
        userId,
        acknowledgedRepeatRecordsByGroup,
      ).percent,
    [acknowledgedRepeatRecordsByGroup, allFields, dashboard?.items, fieldStates, userId],
  );
  const { completionByPage, progressByPage } = useMemo(() => {
    const progress: Record<string, RailProgressState> = {};
    const completion: Record<string, boolean> = {};
    const completedPages = new Set(acknowledgedConfig.completedPages);
    for (const canvas of form?.canvases ?? []) {
      if (!canvas.is_active || !canvas.canvas_key) continue;
      if (canvas.canvas_key === 'F1') {
        progress.F1 = completedPages.has('F1') ? 'completed' : 'notStarted';
        completion.F1 = completedPages.has('F1');
        continue;
      }
      if (canvas.canvas_type === 'summary') continue;
      if (canvas.canvas_key === 'K1') {
        const hasUnsavedConfiguration = !configurationsMatch(config, acknowledgedConfig);
        const availityUnresolved = availityDirty || !availityValid;
        progress.K1 =
          hasUnsavedConfiguration || payerRosterDirty || availityUnresolved
            ? 'inProgress'
            : gateOpen
              ? 'completed'
              : 'notStarted';
        completion.K1 =
          gateOpen && !hasUnsavedConfiguration && !payerRosterDirty && !availityUnresolved;
        continue;
      }
      if (canvas.canvas_key === 'M5') {
        progress.M5 =
          providerRosterEntries.length > 0 && completedPages.has('M5')
            ? 'completed'
            : providerRosterEntries.length > 0
              ? 'inProgress'
              : 'notStarted';
        completion.M5 = providerRosterEntries.length === 0 || progress.M5 === 'completed';
        continue;
      }
      if (canvas.canvas_type === 'upload') {
        const requiredFields = requiredDocumentFields(canvas.fields ?? []);
        const requiredItems = new Map(
          requiredFields.map(field => {
            const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
            return [
              item?.intake_item_id ?? `${field.view}:${field.requirement_key ?? field.field_id}`,
              item,
            ];
          }),
        );
        const complete = [...requiredItems.values()].filter(item => item?.is_complete).length;
        progress[canvas.canvas_key] =
          requiredItems.size === 0
            ? 'notStarted'
            : complete === requiredItems.size
              ? 'completed'
              : complete > 0
                ? 'inProgress'
                : 'notStarted';
        completion[canvas.canvas_key] = requiredItems.size === 0 || complete === requiredItems.size;
        continue;
      }
      const durableCompletion = durableRequiredCompletion(
        canvas.fields ?? [],
        fieldStates,
        dashboard?.items ?? [],
        userId,
        acknowledgedRepeatRecordsByGroup,
      );
      const draftStates = Object.fromEntries(
        (canvas.fields ?? []).flatMap(field => {
          const state = fieldStates[field.field_id];
          if (!state) return [];
          const draftIsDirty = !intakeFieldValuesEqual(state.draftValue, state.acknowledgedValue);
          return [
            [
              field.field_id,
              {
                ...state,
                acknowledgedValue: state.draftValue,
                hasAcknowledgedValue: state.hasAcknowledgedValue || draftIsDirty,
              },
            ],
          ];
        }),
      );
      const draftCompletion = durableRequiredCompletion(
        canvas.fields ?? [],
        draftStates,
        dashboard?.items ?? [],
        userId,
        repeatRecordsByGroup,
      );
      const hasDirtyFields = (canvas.fields ?? []).some(field => {
        const state = fieldStates[field.field_id];
        return state && !intakeFieldValuesEqual(state.draftValue, state.acknowledgedValue);
      });
      const hasDirtyRepeatGroups = (canvas.fields ?? []).some(field => {
        if (!field.repeat_scope || field.repeat_scope === 'none') return false;
        const groupKey = repeatGroupKey(field);
        return !repeatRecordsEqual(
          repeatRecordsByGroup[groupKey] ?? [],
          acknowledgedRepeatRecordsByGroup[groupKey] ?? [],
        );
      });
      const hasAcknowledgedFields = (canvas.fields ?? []).some(
        field => fieldStates[field.field_id]?.hasAcknowledgedValue,
      );
      const complete =
        durableCompletion.total > 0 &&
        !hasDirtyFields &&
        !hasDirtyRepeatGroups &&
        durableCompletion.complete === durableCompletion.total &&
        draftCompletion.complete === draftCompletion.total;
      const hasProgress =
        hasDirtyFields ||
        hasDirtyRepeatGroups ||
        hasAcknowledgedFields ||
        durableCompletion.complete > 0 ||
        draftCompletion.complete > 0;
      progress[canvas.canvas_key] = complete
        ? 'completed'
        : hasProgress
          ? 'inProgress'
          : 'notStarted';
      completion[canvas.canvas_key] = durableCompletion.total === 0 || complete;
    }

    // F1 ("What we'll need") is a fixed rail page, not a generated canvas, so
    // it's never visited by the loop above in production — its completion state
    // lives only in acknowledgedConfig.completedPages. The `progress.F1 === undefined`
    // guard only backfills that missing case: if a canvas keyed 'F1' ever *does* appear
    // (as today's tests synthesize), the loop's own branch already set progress.F1 and
    // completion.F1, and this must not clobber that value.
    if (progress.F1 === undefined) {
      progress.F1 = completedPages.has('F1') ? 'completed' : 'notStarted';
      completion.F1 = completedPages.has('F1');
    }

    for (const reviewPage of [
      { pageKey: 'M6', sectionKey: 'clinic_setup' },
      { pageKey: 'P6', sectionKey: 'your_intake' },
    ] as const) {
      const reviewCanvas = (form?.canvases ?? []).find(
        canvas =>
          canvas.is_active &&
          canvas.canvas_type === 'summary' &&
          canvas.canvas_key === reviewPage.pageKey,
      );
      if (!reviewCanvas) continue;
      const sectionPageKeys = (form?.canvases ?? [])
        .filter(
          canvas =>
            canvas.is_active &&
            canvas.canvas_type !== 'summary' &&
            canvas.section === reviewPage.sectionKey &&
            Boolean(canvas.canvas_key),
        )
        .flatMap(canvas => (canvas.canvas_key ? [canvas.canvas_key] : []))
        .filter(pageKey => !notNeededKeys.has(pageKey));
      const sectionComplete =
        sectionPageKeys.length > 0 && sectionPageKeys.every(pageKey => completion[pageKey]);
      const sectionStarted = sectionPageKeys.some(
        pageKey => progress[pageKey] !== undefined && progress[pageKey] !== 'notStarted',
      );
      progress[reviewPage.pageKey] =
        sectionComplete && completedPages.has(reviewPage.pageKey)
          ? 'completed'
          : sectionStarted
            ? 'inProgress'
            : 'notStarted';
      completion[reviewPage.pageKey] = sectionComplete && completedPages.has(reviewPage.pageKey);
    }
    return { completionByPage: completion, progressByPage: progress };
  }, [
    acknowledgedConfig,
    acknowledgedRepeatRecordsByGroup,
    availityDirty,
    availityValid,
    config,
    dashboard?.items,
    fieldStates,
    form?.canvases,
    gateOpen,
    notNeededKeys,
    payerRosterDirty,
    providerRosterEntries.length,
    repeatRecordsByGroup,
    userId,
  ]);
  const activeCompletionPageKeys = useMemo(
    () =>
      (form?.canvases ?? [])
        .filter(
          canvas =>
            canvas.is_active &&
            Boolean(canvas.canvas_key) &&
            ['clinic_setup', 'your_intake'].includes(canvas.section),
        )
        .flatMap(canvas => (canvas.canvas_key ? [canvas.canvas_key] : [])),
    [form?.canvases],
  );
  const isCompletionPageComplete = useCallback(
    (pageKey: string) => completionByPage[pageKey] ?? false,
    [completionByPage],
  );
  const completionReady = useMemo(() => {
    const reviewPageKeys = activeCompletionPageKeys.filter(pageKey =>
      ['M6', 'P6'].includes(pageKey),
    );
    if (reviewPageKeys.length === 0) return false;
    // K1 lives in the key_questions section, so it never appears in
    // activeCompletionPageKeys (clinic_setup/your_intake only) — gate finish
    // on Availity's own required-ness directly rather than pulling all of
    // K1's broader config-dirty state into this check.
    if (availityField && (availityDirty || !availityValid)) return false;

    return activeCompletionPageKeys.every(isCompletionPageComplete);
  }, [
    activeCompletionPageKeys,
    availityDirty,
    availityField,
    availityValid,
    isCompletionPageComplete,
  ]);
  const completionPageReachable = completionReady || adminVerificationNeedsCompletionPage;
  const firstIncompleteCompletionPageKey = useMemo(
    () =>
      ORDERED_KEYS.find(pageKey => {
        if (!activeCompletionPageKeys.includes(pageKey)) return false;
        return !isCompletionPageComplete(pageKey);
      }) ?? 'K1',
    [activeCompletionPageKeys, isCompletionPageComplete],
  );

  useEffect(() => {
    if (isAggregateView) return;
    if (hydratedConfigurationScope !== configurationScope || !form) return;
    if (currentKey !== 'X1' || completionPageReachable) return;
    navigateToPage(firstIncompleteCompletionPageKey, true);
  }, [
    completionPageReachable,
    configurationScope,
    currentKey,
    firstIncompleteCompletionPageKey,
    form,
    hydratedConfigurationScope,
    isAggregateView,
    navigateToPage,
  ]);

  const reviewCanvases = useMemo(() => {
    const sectionKey =
      currentKey === 'M6' ? 'clinic_setup' : currentKey === 'P6' ? 'your_intake' : null;
    if (!sectionKey) return [];
    const sectionItems = RAIL_SECTIONS.find(entry => entry.key === sectionKey)?.items ?? [];
    const pageOrder = new Map(sectionItems.map((item, index) => [item.pageKey, index]));
    return (form?.canvases ?? [])
      .filter(
        canvas =>
          canvas.is_active &&
          canvas.section === sectionKey &&
          canvas.canvas_type !== 'summary' &&
          Boolean(canvas.canvas_key),
      )
      .sort(
        (left, right) =>
          (pageOrder.get(left.canvas_key ?? '') ?? Number.MAX_SAFE_INTEGER) -
          (pageOrder.get(right.canvas_key ?? '') ?? Number.MAX_SAFE_INTEGER),
      );
  }, [currentKey, form?.canvases]);
  const reviewItems = useMemo(() => {
    const sectionKey =
      currentKey === 'M6' ? 'clinic_setup' : currentKey === 'P6' ? 'your_intake' : null;
    if (!sectionKey) return [];
    const reviewPageKey = sectionKey === 'clinic_setup' ? 'M6' : 'P6';
    const section = RAIL_SECTIONS.find(entry => entry.key === sectionKey);
    return (section?.items ?? [])
      .filter(item => item.pageKey !== reviewPageKey && !notNeededKeys.has(item.pageKey))
      .filter(item => reviewCanvases.some(canvas => canvas.canvas_key === item.pageKey))
      .map(item => {
        const state: RailProgressState = completionByPage[item.pageKey]
          ? 'completed'
          : (progressByPage[item.pageKey] ?? 'notStarted');
        return { pageKey: item.pageKey, label: item.label, state };
      });
  }, [completionByPage, currentKey, notNeededKeys, progressByPage, reviewCanvases]);

  const foundDocuments: FoundDocument[] = useMemo(() => {
    const persisted = (documentInventory?.documents ?? []).map(documentItem =>
      persistedDocumentToFoundDocument(documentItem, allFields),
    );
    const persistedArtifactIds = new Set(
      (documentInventory?.documents ?? []).flatMap(documentItem => {
        const artifactId = documentItem.document_reference.source_artifact_id;
        return artifactId ? [artifactId] : [];
      }),
    );
    const optimistic = pendingUploads
      .filter(pending => !persistedArtifactIds.has(pending.artifactId))
      .map(pending => {
        const info = docKinds[pending.artifactId];
        return {
          artifactId: pending.artifactId,
          filename: pending.filename,
          status: documentDisplayStatus(pending.status),
          docKind: info?.docKind ?? null,
          filledCount: info?.filled ?? 0,
          understood: pending.status !== 'failed',
        };
      });
    return [...optimistic, ...persisted];
  }, [pendingUploads, docKinds, documentInventory?.documents, allFields]);
  const isDocumentProcessing =
    isUploadInFlight || foundDocuments.some(document => document.status === 'parsing');

  // Same object shape (and construction) as `foundDocuments`, keyed by
  // `document_reference_id` so a satisfied requirement row can resolve the
  // exact FoundDocument the "Documents received" list would render for it,
  // and open it through the same preview dialog via `handleOpenDocument`.
  const documentByReferenceId = useMemo(
    () =>
      Object.fromEntries(
        (documentInventory?.documents ?? []).map(documentItem => [
          documentItem.document_reference.document_reference_id,
          persistedDocumentToFoundDocument(documentItem, allFields),
        ]),
      ),
    [documentInventory?.documents, allFields],
  );

  // ---- navigation --------------------------------------------------------
  const goNext = useCallback(() => {
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let next = index + 1; next < ORDERED_KEYS.length; next += 1) {
      const key = ORDERED_KEYS[next];
      if (!notNeededKeys.has(key)) {
        navigateToPage(key);
        return;
      }
    }
  }, [currentKey, navigateToPage, notNeededKeys]);

  const goBack = useCallback(() => {
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let previous = index - 1; previous >= 0; previous -= 1) {
      const key = ORDERED_KEYS[previous];
      if (!notNeededKeys.has(key)) {
        navigateToPage(key);
        return;
      }
    }
  }, [currentKey, navigateToPage, notNeededKeys]);

  const handleCompletedPageContinue = useCallback(
    async (pageKey: IntakeConfig['completedPages'][number]) => {
      if (acknowledgedConfig.completedPages.includes(pageKey)) {
        goNext();
        return;
      }
      const nextConfig = {
        ...config,
        completedPages: [...new Set([...config.completedPages, pageKey])],
      };
      setConfig(nextConfig);

      // Admin browsing without a specific provider selected has nothing to
      // persist the completion against. Reflect it locally and advance
      // instead of silently stranding the user on the current page.
      if (isAdmin && (context.kind !== 'admin' || !context.userId)) {
        setAcknowledgedConfig(nextConfig);
        goNext();
        return;
      }
      setIsSavingPageCompletion(true);
      setAdminSaveErrorCount(0);
      setAdminSaveErrorMessage(undefined);
      setCompletionErrorPageKey(null);
      try {
        if (context.kind === 'admin') {
          await updateAdminConfiguration({
            organizationId: context.organizationId,
            userId: context.userId ?? '',
            intakeFormConfigurationUpdateRequest: intakeConfigurationUpdate(nextConfig),
          }).unwrap();
        } else {
          await saveProviderConfigurationImmediately(nextConfig);
        }
        setAcknowledgedConfig(nextConfig);
        goNext();
      } catch {
        setConfig(previous => ({
          ...previous,
          completedPages: previous.completedPages.filter(key => key !== pageKey),
        }));
        setCompletionErrorPageKey(pageKey);
      } finally {
        setIsSavingPageCompletion(false);
      }
    },
    [
      acknowledgedConfig,
      config,
      context,
      goNext,
      isAdmin,
      saveProviderConfigurationImmediately,
      updateAdminConfiguration,
    ],
  );

  const nextLabel = useMemo(() => {
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let next = index + 1; next < ORDERED_KEYS.length; next += 1) {
      const key = ORDERED_KEYS[next];
      if (notNeededKeys.has(key)) continue;
      const label = RAIL_SECTIONS.flatMap(section => section.items).find(
        item => item.pageKey === key,
      )?.label;
      return `Next: ${label} →`;
    }
    return 'Finish →';
  }, [currentKey, notNeededKeys]);

  const currentLabel =
    RAIL_SECTIONS.flatMap(section => section.items).find(item => item.pageKey === currentKey)
      ?.label ?? currentKey;

  const currentPage = useMemo(
    () => (form?.canvases ?? []).find(canvas => canvas.canvas_key === currentKey),
    [form, currentKey],
  );
  const currentFields = useMemo(() => currentPage?.fields ?? [], [currentPage?.fields]);
  const currentGroups = currentPage?.groups ?? [];
  // Availity (I23) renders on K1 via allFields rather than K1's own canvas
  // fields, so it still works during a mixed-deploy window where an older
  // backend still serves I23 on the M4 canvas. Admin's explicit save only
  // persists the fields it's given, so make sure Availity rides along when
  // it isn't already part of K1's own field list — otherwise a checked box
  // saves silently without persisting.
  const k1SaveFields = useMemo(() => {
    if (!availityField || currentFields.some(field => field.field_id === availityField.field_id)) {
      return currentFields;
    }
    return [...currentFields, availityField];
  }, [availityField, currentFields]);
  const draftValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [fieldId, state.draftValue]),
      ),
    [fieldStates],
  );
  const acknowledgedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [fieldId, state.acknowledgedValue]),
      ),
    [fieldStates],
  );
  const hasAcknowledgedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [
          fieldId,
          state.hasAcknowledgedValue,
        ]),
      ),
    [fieldStates],
  );
  const disabledReasons = useMemo(() => {
    const reasons: Record<string, string> = {};
    for (const field of allFields) {
      // IntakePageFieldInput owns catalog-declared read-only fields. Everything
      // writable is fail-closed here unless this component has a persistence
      // branch for the exact destination below.
      if (field.is_writable === false) continue;
      if (isAggregateView) {
        reasons[field.field_id] = 'Select a provider to edit this information.';
        continue;
      }

      if (field.destination.kind === 'intake_item_evidence') {
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!field.requirement_key || !item?.intake_item_id) {
          reasons[field.field_id] = 'This field is still being prepared. Refresh and try again.';
        }
        continue;
      }

      if (field.destination.kind === 'intake_item_sensitive') {
        if (isAdmin) {
          const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
          reasons[field.field_id] = item?.sensitive_fields_on_file?.includes(
            field.destination.field_key,
          )
            ? 'Secure value is on file; staff cannot replace it.'
            : 'The provider must complete this field through the secure flow.';
          continue;
        }
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!field.requirement_key || !item?.intake_item_id) {
          reasons[field.field_id] = 'This field is still being prepared. Refresh and try again.';
        }
        continue;
      }

      // intake_config, document_reference, and future destination kinds do
      // not have a generic page write path in handlePersistField.
      reasons[field.field_id] = 'Complete this information in its dedicated Intake Hub section.';
    }
    return reasons;
  }, [allFields, dashboard?.items, isAdmin, isAggregateView, userId]);

  const maskedSensitiveValues = useMemo(
    () => maskedSensitiveValuesByFieldId(allFields, dashboard?.items ?? [], userId),
    [allFields, dashboard?.items, userId],
  );

  const navigateFromRail = useCallback(
    (pageKey: string) => {
      if (pageKey === 'X1' && !completionPageReachable && !isAggregateView) {
        navigateToPage(firstIncompleteCompletionPageKey);
        return;
      }
      navigateToPage(pageKey);
    },
    [completionPageReachable, firstIncompleteCompletionPageKey, isAggregateView, navigateToPage],
  );

  const handleGoToDashboard = useCallback(() => {
    intakeHub.completionDashboardClicked({
      active_page_count: activeCompletionPageKeys.length,
      completed_page_count: activeCompletionPageKeys.filter(isCompletionPageComplete).length,
      percent_complete: percentComplete,
    });
    navigate('/dashboard');
  }, [activeCompletionPageKeys, intakeHub, isCompletionPageComplete, navigate, percentComplete]);

  const handleDraftChange = useCallback(
    (field: GeneratedIntakeField, value: IntakeFieldValue) => {
      setFieldStates(previous =>
        updateFieldStatesForDraftChange(allFields, previous, field, value),
      );
    },
    [allFields],
  );

  const handlePersistField = useCallback(
    async (field: GeneratedIntakeField, value: IntakeFieldValue) => {
      if (!isAdmin) {
        intakeHub.fieldEditAttempted({
          destination_kind: field.destination.kind,
          field_id: field.field_id,
          field_type: field.data_type,
        });
      }

      if (field.destination.kind === 'intake_item_evidence') {
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!item?.intake_item_id || !form?.catalog_version) {
          throw new Error('Intake field destination is unavailable');
        }
        const intakeItemId = item.intake_item_id;
        let persistedResponse;
        if (context.kind === 'admin') {
          const adminUserId = context.userId;
          if (!adminUserId) {
            throw new Error('Intake evidence changes require an explicitly selected user');
          }
          persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
            updateAdminEvidence({
              organizationId: context.organizationId,
              userId: adminUserId,
              intakeItemId,
              updateItemEvidenceRequest: {
                catalog_version: form.catalog_version,
                extracted_fields: { [field.destination.field_key]: value },
              },
            }).unwrap(),
          );
        } else {
          persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
            updateProviderEvidence({
              intakeItemId,
              updateItemEvidenceProviderRequest: {
                catalog_version: form.catalog_version,
                extracted_fields: { [field.destination.field_key]: value },
              },
            }).unwrap(),
          );
        }
        storeAcceptedItem(persistedResponse.item);
        setFieldStates(previous => acknowledgePersistedFieldValue(previous, field.field_id, value));
        return;
      }

      if (field.destination.kind === 'intake_item_sensitive') {
        if (isAdmin) throw new Error('Secure fields are provider-only');
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!item?.intake_item_id || typeof value !== 'string') {
          throw new Error('Secure field destination is unavailable');
        }
        const intakeItemId = item.intake_item_id;
        const fieldKey = field.destination.field_key;
        await writeQueue.enqueue(intakeItemId, () =>
          updateSensitiveField({
            intakeItemId,
            updateSensitiveFieldRequest: {
              field_key: fieldKey,
              value,
            },
          }).unwrap(),
        );
        setPendingSensitiveConfirmations(previous => new Set(previous).add(field.field_id));
        setFieldStates(previous =>
          acknowledgePersistedFieldValue(previous, field.field_id, value, true),
        );
        void refetchDashboard();
        return;
      }

      throw new Error('Intake field destination is not writable here');
    },
    [
      form?.catalog_version,
      context,
      dashboard?.items,
      isAdmin,
      refetchDashboard,
      storeAcceptedItem,
      intakeHub,
      updateAdminEvidence,
      updateProviderEvidence,
      updateSensitiveField,
      writeQueue,
      userId,
    ],
  );

  const handlePersistRepeatGroup = useCallback(
    async (fields: GeneratedIntakeField[], records: IntakeRepeatRecord[]) => {
      const field = fields[0];
      if (!field || field.destination.kind !== 'intake_item_evidence') {
        throw new Error('Repeated intake field destination is unavailable');
      }
      const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
      if (!item?.intake_item_id || !form?.catalog_version) {
        throw new Error('Repeated intake field destination is unavailable');
      }
      const groupKey = repeatGroupKey(field);
      const serverRecords = serverRepeatRecordsByGroup[groupKey] ?? records;
      setRepeatGroupStates(previous => {
        const existing = previous[groupKey] ?? {
          acknowledgedRecords: serverRecords,
          draftRecords: serverRecords,
        };
        return {
          ...previous,
          [groupKey]: { ...existing, draftRecords: records },
        };
      });
      const intakeItemId = item.intake_item_id;
      const extractedFields = repeatRecordsEvidence(records);
      let persistedResponse;
      if (context.kind === 'admin') {
        if (!context.userId) {
          throw new Error('Intake evidence changes require an explicitly selected user');
        }
        persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
          updateAdminEvidence({
            organizationId: context.organizationId,
            userId: context.userId,
            intakeItemId,
            updateItemEvidenceRequest: {
              catalog_version: form.catalog_version,
              extracted_fields: extractedFields,
            },
          }).unwrap(),
        );
      } else {
        persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
          updateProviderEvidence({
            intakeItemId,
            updateItemEvidenceProviderRequest: {
              catalog_version: form.catalog_version,
              extracted_fields: extractedFields,
            },
          }).unwrap(),
        );
      }
      storeAcceptedItem(persistedResponse.item);
      setRepeatGroupStates(previous => {
        const existing = previous[groupKey] ?? {
          acknowledgedRecords: serverRecords,
          draftRecords: records,
        };
        return {
          ...previous,
          [groupKey]: {
            acknowledgedRecords: records,
            draftRecords: repeatRecordsEqual(existing.draftRecords, records)
              ? records
              : existing.draftRecords,
          },
        };
      });
      void refetchDashboard();
    },
    [
      context,
      dashboard?.items,
      form?.catalog_version,
      refetchDashboard,
      serverRepeatRecordsByGroup,
      storeAcceptedItem,
      updateAdminEvidence,
      updateProviderEvidence,
      userId,
      writeQueue,
    ],
  );

  const handlePersistPayerRoster = useCallback(
    async (rows: IntakePayerRosterEntry[]) => {
      if (context.kind !== 'admin') {
        throw new Error('Payer roster changes require staff access');
      }
      const catalogVersion =
        form?.catalog_version ?? payerRosterItem?.evidence_summary?.catalog_version;
      if (!payerRosterItem?.intake_item_id) {
        throw new Error('Payer roster destination is unavailable');
      }
      if (!catalogVersion) {
        throw new Error('Payer roster catalog version is unavailable');
      }
      const intakeItemId = payerRosterItem.intake_item_id;
      const persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
        updateAdminEvidence({
          organizationId: context.organizationId,
          intakeItemId,
          updateItemEvidenceRequest: {
            catalog_version: catalogVersion,
            payer_roster: rows,
          },
        }).unwrap(),
      );
      storeAcceptedItem(persistedResponse.item);
      setAcknowledgedPayerRoster(rows);
      void refetchDashboard();
    },
    [
      context,
      form?.catalog_version,
      payerRosterItem?.evidence_summary?.catalog_version,
      payerRosterItem?.intake_item_id,
      refetchDashboard,
      storeAcceptedItem,
      updateAdminEvidence,
      writeQueue,
    ],
  );

  const handleConfigurationChange = useCallback(
    (patch: Partial<IntakeConfig>) => {
      if (isAggregateView) return;
      setConfig(previous => {
        const next = { ...previous, ...patch };
        if (!isAdmin) triggerProviderConfigurationSave(next);
        return next;
      });
    },
    [isAdmin, isAggregateView, triggerProviderConfigurationSave],
  );

  const handleAddPayer = useCallback((payer: PayerRow) => {
    setPayerRosterDraft(previous => [
      ...previous,
      {
        enrollment_status: payer.enrollmentStatus,
        jotbill: payer.jotbill,
        jotcred: payer.jotcred,
        payer_id: payer.payerId,
        payer_name: payer.displayName,
      },
    ]);
  }, []);

  const handleRemovePayer = useCallback((payerId: string) => {
    setPayerRosterDraft(previous => previous.filter(payer => payer.payer_id !== payerId));
  }, []);

  const handlePayerEnrollmentStatusChange = useCallback(
    (payerId: string, enrollmentStatus: PayerRow['enrollmentStatus']) => {
      setPayerRosterDraft(previous =>
        previous.map(payer =>
          payer.payer_id === payerId ? { ...payer, enrollment_status: enrollmentStatus } : payer,
        ),
      );
    },
    [],
  );

  const handlePayerServiceChange = useCallback(
    (payerId: string, service: 'jotcred' | 'jotbill', enabled: boolean) => {
      setPayerRosterDraft(previous =>
        previous.map(payer =>
          payer.payer_id === payerId ? { ...payer, [service]: enabled } : payer,
        ),
      );
    },
    [],
  );

  const handleSaveAdminChanges = useCallback(
    async (fieldsToSave: GeneratedIntakeField[]) => {
      const dirtyFields = fieldsToSave.filter(field => {
        const state = fieldStates[field.field_id];
        return (
          state &&
          JSON.stringify(state.draftValue) !== JSON.stringify(state.acknowledgedValue) &&
          !disabledReasons[field.field_id]
        );
      });
      const configurationIsDirty = !configurationsMatch(config, acknowledgedConfig);
      if (dirtyFields.length === 0 && !configurationIsDirty && !payerRosterDirty) return;

      setIsSavingAdminChanges(true);
      setAdminSaveErrorCount(0);
      setAdminSaveErrorMessage(undefined);
      const saves: Array<{ kind: AdminSaveKind; promise: Promise<unknown> }> = dirtyFields.map(
        field => ({
          kind: 'field',
          promise: handlePersistField(field, fieldStates[field.field_id].draftValue),
        }),
      );
      if (payerRosterDirty) {
        saves.push({ kind: 'payer', promise: handlePersistPayerRoster(payerRosterDraft) });
      }
      if (configurationIsDirty && context.kind === 'admin' && context.userId) {
        saves.push({
          kind: 'configuration',
          promise: updateAdminConfiguration({
            organizationId: context.organizationId,
            userId: context.userId,
            intakeFormConfigurationUpdateRequest: intakeConfigurationUpdate(config),
          })
            .unwrap()
            .then(() => setAcknowledgedConfig(config)),
        });
      }
      const results = await Promise.allSettled(saves.map(save => save.promise));
      const failedKinds = results.flatMap((result, index) =>
        result.status === 'rejected' ? [saves[index].kind] : [],
      );
      setAdminSaveErrorCount(failedKinds.length);
      setAdminSaveErrorMessage(formatAdminSaveErrorMessage(failedKinds));
      setIsSavingAdminChanges(false);
    },
    [
      acknowledgedConfig,
      config,
      context,
      disabledReasons,
      fieldStates,
      handlePersistField,
      handlePersistPayerRoster,
      payerRosterDirty,
      payerRosterDraft,
      updateAdminConfiguration,
    ],
  );

  const resolveAcceptedSuggestionRequests = useCallback(
    (suggestions: IntakeFieldSuggestion[]): IntakeFieldSuggestionAcceptanceRequest[] =>
      suggestions.map(suggestion => {
        const documentReferenceId = documentSuggestionReferenceIds[suggestion.field_id];
        if (!documentReferenceId) throw new Error('Document suggestion is unavailable');
        return {
          intake_item_id: suggestion.target_intake_item_id,
          document_reference_id: documentReferenceId,
          extraction_id: suggestion.extraction_id,
          field_id: suggestion.field_id,
          /* The contract's compare-and-swap pairing: the suggestion response
             now carries the field's current_value, and acceptance echoes it
             back so the server can reject if the field changed since the
             suggestion was computed. */
          expected_current_value: suggestion.current_value,
          ...(suggestion.repeat_record_id ? { repeat_record_id: suggestion.repeat_record_id } : {}),
        };
      }),
    [documentSuggestionReferenceIds],
  );

  const enqueueSuggestionAcceptance = useCallback(
    (operationId: string, write: () => Promise<void>): Promise<void> => {
      const existing = acceptanceOperations.current.get(operationId);
      if (existing) return existing;

      const operation = acceptanceOperationTail.current.then(write);
      acceptanceOperations.current.set(operationId, operation);
      const normalizedTail = operation.then(
        () => undefined,
        () => undefined,
      );
      acceptanceOperationTail.current = normalizedTail;
      void normalizedTail.then(() => {
        if (acceptanceOperationTail.current === normalizedTail) {
          acceptedSuggestionRequestIds.current.clear();
        }
      });
      void operation
        .finally(() => {
          if (acceptanceOperations.current.get(operationId) === operation) {
            acceptanceOperations.current.delete(operationId);
          }
        })
        .catch(() => undefined);
      return operation;
    },
    [],
  );

  const persistAcceptedSuggestions = useCallback(
    async (
      suggestions: IntakeFieldSuggestion[],
      requests: IntakeFieldSuggestionAcceptanceRequest[],
    ) => {
      if (isAdmin) throw new Error('Document suggestions are provider-only');
      const response = await writeQueue.enqueueMany(
        suggestions.map(suggestion => suggestion.target_intake_item_id),
        () =>
          acceptDocumentFieldSuggestions({
            acceptDocumentFieldSuggestionsRequest: { suggestions: requests },
          }).unwrap(),
      );
      return { requests, response };
    },
    [acceptDocumentFieldSuggestions, isAdmin, writeQueue],
  );

  const handleAcceptSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) => {
      let requests: IntakeFieldSuggestionAcceptanceRequest[];
      try {
        requests = resolveAcceptedSuggestionRequests([suggestion]);
      } catch {
        void refetchDashboard();
        void refetchDocumentInventory();
        return Promise.reject(new SuggestionAcceptanceError('uncertain'));
      }
      const requestId = acceptanceRequestIdentity(requests[0]);
      const operationId = acceptanceOperationIdentity(requests);

      return enqueueSuggestionAcceptance(operationId, async () => {
        if (acceptedSuggestionRequestIds.current.has(requestId)) return;
        intakeHub.suggestionActionClicked({ action: 'accept', suggestion_count: 1 });
        let persisted;
        try {
          persisted = await persistAcceptedSuggestions([suggestion], requests);
        } catch {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('uncertain');
        }
        if (!hasExactlyCorrelatedAcceptanceResults(persisted.requests, persisted.response)) {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('uncertain');
        }
        if (persisted.response.results[0].outcome !== 'accepted') {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('stale');
        }
        const acceptedItem = persisted.response.results[0].item;
        if (!acceptedItem) {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('uncertain');
        }
        storeAcceptedItem(acceptedItem);
        acceptedSuggestionRequestIds.current.add(requestId);
        setFieldStates(previous =>
          replaceFieldValueWithAcceptedSuggestion(previous, suggestion.field_id, suggestion.value),
        );
      });
    },
    [
      enqueueSuggestionAcceptance,
      intakeHub,
      persistAcceptedSuggestions,
      refetchDashboard,
      refetchDocumentInventory,
      resolveAcceptedSuggestionRequests,
      storeAcceptedItem,
    ],
  );

  const handleDiscardSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) => {
      intakeHub.suggestionActionClicked({ action: 'discard', suggestion_count: 1 });
      setDiscardedSuggestionIds(previous => {
        const next = new Set(previous);
        next.add(suggestionIdentity(suggestion));
        return next;
      });
    },
    [intakeHub],
  );

  const handleHelpAction = useCallback(
    (action: 'message_specialist') => {
      if (action === 'message_specialist') dispatch(openChatWindow());
    },
    [dispatch],
  );

  if (materializationStatus === 'failed' && hasMissingMaterializedDestinations) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void runMaterialization()}>
              Retry item preparation
            </Button>
          }
        >
          We couldn&apos;t prepare your intake items.
        </Alert>
      </Box>
    );
  }

  if (dashboardError && !dashboard) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={isDashboardFetching}
              onClick={() => void refetchDashboard()}
            >
              Retry
            </Button>
          }
        >
          We couldn&apos;t load your intake. Please try again.
        </Alert>
      </Box>
    );
  }

  if (configurationError && hydratedConfigurationScope !== configurationScope) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetchConfiguration()}>
              Retry
            </Button>
          }
        >
          We couldn&apos;t load this intake configuration. No changes have been made.
        </Alert>
      </Box>
    );
  }

  if (
    isPreparingMissingItems ||
    (isConfigurationFetching && hydratedConfigurationScope !== configurationScope) ||
    hydratedConfigurationScope !== configurationScope ||
    (!serverConfiguration && !isAggregateView)
  ) {
    return (
      <LinearProgress
        aria-label={
          isPreparingMissingItems ? 'Preparing intake items' : 'Loading intake configuration'
        }
      />
    );
  }

  return (
    <IntakeShell
      organizationName={dashboard?.clinician?.organization_id ? 'Your practice' : 'Your practice'}
      currentPageKey={currentKey}
      notNeededKeys={notNeededKeys}
      gateOpen={gateOpen}
      percentComplete={percentComplete}
      onNavigate={navigateFromRail}
      pendingByPage={suggestionsByPage}
      progressByPage={progressByPage}
      lockedPageKeys={completionPageReachable || isAggregateView ? undefined : new Set(['X1'])}
    >
      {currentKey === 'K1' && (
        <>
          {!isAdmin && !isAggregateView && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <AutosaveStatus
                status={providerConfigurationSaveStatus}
                isDirty={hasPendingProviderConfigurationSave()}
                lastSavedAt={providerConfigurationLastSavedAt}
                verbose
              />
            </Box>
          )}
          <IntakeConfigPage
            config={effectiveConfig}
            onChange={handleConfigurationChange}
            payerRows={payerRows}
            payerTableProps={{
              canManage: isAdmin,
              loading: isDashboardFetching && !dashboard,
              onEnrollmentStatusChange: isAdmin ? handlePayerEnrollmentStatusChange : undefined,
              onRemove: isAdmin ? handleRemovePayer : undefined,
              onServiceChange: isAdmin ? handlePayerServiceChange : undefined,
              search: isAdmin ? (
                <IntakeAdminPayerSearch existingRows={payerRows} onAdd={handleAddPayer} />
              ) : undefined,
            }}
            eligibleProviders={eligibleProviders}
            eligibleProvidersLoading={eligibleProvidersLoading}
            eligibleProvidersError={Boolean(eligibleProvidersQueryError)}
            rosterCandidates={rosterCandidates}
            canManageRoster={canManageRoster}
            onAddRosterProvider={handleAddRosterProvider}
            onRemoveRosterProvider={handleRemoveRosterProvider}
            isAddingRosterProvider={isAddingRosterProvider}
            derivedSentence={derivedSentence}
            groups={currentGroups}
            onHelpAction={handleHelpAction}
            organizationScope={isAggregateView}
            readOnlyReason={
              isAggregateView
                ? 'These settings belong to the organization. Provider sections remain grouped by provider.'
                : undefined
            }
            continueLabel={isAggregateView ? null : "See what we'll need →"}
            onContinue={() => {
              setGateOpenedInSession(true);
              navigateToPage('F1');
            }}
            saveLabel={isAdmin ? (isSavingAdminChanges ? 'Saving…' : 'Save changes') : undefined}
            onSave={isAdmin ? () => void handleSaveAdminChanges(k1SaveFields) : undefined}
            saveDisabled={
              isAdmin &&
              (isSavingAdminChanges ||
                (configurationsMatch(config, acknowledgedConfig) &&
                  !payerRosterDirty &&
                  !availityDirty))
            }
            saveErrorMessage={isAdmin ? adminSaveErrorMessage : undefined}
            availityField={availityField}
            draftValues={draftValues}
            acknowledgedValues={acknowledgedValues}
            onDraftChange={handleDraftChange}
            onPersistField={handlePersistField}
            disabledReasons={disabledReasons}
            saveMode={isAdmin ? 'explicit' : 'autosave'}
            isSaving={isSavingAdminChanges}
            saveErrorCount={adminSaveErrorCount}
          />
        </>
      )}

      {isAggregateView && currentKey !== 'K1' && context.kind === 'admin' && (
        <AggregateIntakePage
          aggregate={aggregateView}
          currentPageKey={currentKey}
          error={aggregateViewError}
          isFetching={isAggregateViewFetching}
          organizationId={context.organizationId}
          onPageChange={setAggregatePage}
          onRetry={() => void refetchAggregateView()}
        />
      )}

      {!isAggregateView && currentKey === 'F1' && (
        <IntakeForecastPage
          pages={pageStats}
          documents={documentsToGather}
          totalRequired={totalRequired}
          onContinue={() => {
            // A save is already in flight (isSavingPageCompletion), so a rapid repeat click
            // — before the button below has re-rendered as disabled — must not re-track the
            // event or start a second configuration update. handleCompletedPageContinue's own
            // "already acknowledged" short-circuit only helps *after* the first save resolves;
            // this guards the window while it's still pending.
            if (isSavingPageCompletion || isProviderConfigurationHydrating) return;
            // Only the first acknowledgement is a real conversion event — a revisit to an
            // already-completed F1 short-circuits inside handleCompletedPageContinue and
            // just navigates on, so tracking it again would inflate the metric.
            if (!isAdmin && !acknowledgedConfig.completedPages.includes('F1')) {
              intakeHub.forecastAcknowledgedClicked({ total_required: totalRequired });
            }
            void handleCompletedPageContinue('F1');
          }}
          onBack={goBack}
          nextLabel={nextLabel}
          errorMessage={
            completionErrorPageKey === 'F1'
              ? 'We could not save your acknowledgement. Try again.'
              : undefined
          }
          disabled={isSavingPageCompletion || isProviderConfigurationHydrating}
          disabledReason={
            isSavingPageCompletion
              ? 'Wait for your acknowledgement to finish saving before continuing.'
              : isProviderConfigurationHydrating
                ? 'Loading your configuration before continuing.'
                : undefined
          }
        />
      )}

      {!isAggregateView && currentKey === 'M5' && (
        <IntakeProviderRosterPage
          providers={eligibleProviders}
          isLoading={eligibleProvidersLoading}
          isError={Boolean(eligibleProvidersQueryError)}
          onRetry={() => void refetchProviderCandidates()}
          onContinue={() => {
            if (providerRosterEntries.length === 0) {
              goNext();
              return;
            }
            void handleCompletedPageContinue('M5');
          }}
          onBack={goBack}
          nextLabel={nextLabel}
          errorMessage={
            completionErrorPageKey === 'M5'
              ? 'Your provider roster acknowledgement could not be saved. Try again.'
              : undefined
          }
        />
      )}

      {!isAggregateView && (currentKey === 'M6' || currentKey === 'P6') && (
        <IntakeReviewPage
          sectionLabel={currentKey === 'M6' ? 'Clinic setup' : 'Your intake'}
          items={reviewItems.map(item => {
            const canvas = reviewCanvases.find(entry => entry.canvas_key === item.pageKey);
            const fields = canvas?.fields ?? [];
            const groups = canvas?.groups ?? [];
            const canvasDocuments = foundDocuments.filter(document =>
              documentMatchesFields(document, fields, dashboard?.items ?? [], userId),
            );
            let content: ReactNode;

            if (item.pageKey in DOCUMENT_PAGES) {
              const documentPage = DOCUMENT_PAGES[item.pageKey];
              const requiredItems = new Map<
                string,
                { fields: GeneratedIntakeField[]; intakeItem?: GeneratedIntakeItem }
              >();
              requiredDocumentFields(fields).forEach(field => {
                const intakeItem = findIntakeItemForField(field, dashboard?.items ?? [], userId);
                const key =
                  intakeItem?.intake_item_id ??
                  `${field.view}:${field.requirement_key ?? field.field_id}`;
                const existing = requiredItems.get(key);
                requiredItems.set(key, {
                  fields: [...(existing?.fields ?? []), field],
                  intakeItem,
                });
              });
              const missingRequiredDocumentCount = [...requiredItems.values()].filter(
                requirement =>
                  !requirement.intakeItem?.is_complete &&
                  !canvasDocuments.some(document =>
                    documentMatchesFields(
                      document,
                      requirement.fields,
                      dashboard?.items ?? [],
                      userId,
                    ),
                  ),
              ).length;
              content = (
                <IntakeDocumentsPage
                  title={documentPage.title}
                  sub={documentPage.sub}
                  dropHint={documentPage.hint}
                  documents={canvasDocuments}
                  onDismissFailed={dismissByArtifactId}
                  getDocumentUrl={getDocumentUrl}
                  isDragging={isDragging}
                  isProcessing={isDocumentProcessing}
                  onFiles={handleFiles}
                  landedSummary={landedSummary}
                  groups={groups}
                  onHelpAction={handleHelpAction}
                  onContinue={() => undefined}
                  nextLabel=""
                  embedded
                  missingRequiredDocumentCount={missingRequiredDocumentCount}
                  uploadingCount={uploadingFileCount}
                  showRowDownload={isAdmin}
                  requirements={documentRequirementStatuses(
                    fields,
                    dashboard?.items ?? [],
                    userId,
                    documentByReferenceId,
                  )}
                />
              );
            } else if (item.pageKey === 'M5') {
              content = (
                <IntakeProviderRosterPage
                  providers={eligibleProviders}
                  isLoading={eligibleProvidersLoading}
                  isError={Boolean(eligibleProvidersQueryError)}
                  onRetry={() => void refetchProviderCandidates()}
                  onContinue={() => undefined}
                  onBack={() => undefined}
                  nextLabel=""
                  embedded
                />
              );
            } else {
              content = (
                <IntakeFieldsPage
                  title={canvas?.canvas_name ?? item.label}
                  fields={fields}
                  groups={groups}
                  suggestions={documentSuggestions}
                  onAcceptSuggestion={handleAcceptSuggestion}
                  onDiscardSuggestion={handleDiscardSuggestion}
                  onContinue={() => undefined}
                  nextLabel=""
                  draftValues={draftValues}
                  acknowledgedValues={acknowledgedValues}
                  hasAcknowledgedValues={hasAcknowledgedValues}
                  onDraftChange={handleDraftChange}
                  onPersistField={handlePersistField}
                  repeatRecordsByGroup={repeatRecordsByGroup}
                  onPersistRepeatGroup={handlePersistRepeatGroup}
                  onHelpAction={handleHelpAction}
                  maskedSensitiveValues={maskedSensitiveValues}
                  disabledReasons={disabledReasons}
                  saveMode={isAdmin ? 'explicit' : 'autosave'}
                  isSaving={isSavingAdminChanges}
                  saveErrorCount={adminSaveErrorCount}
                  onSaveChanges={isAdmin ? () => void handleSaveAdminChanges(fields) : undefined}
                  embedded
                  showRequiredErrors
                />
              );
            }

            return { ...item, content };
          })}
          onContinue={() => {
            if (!isAdmin) {
              intakeHub.reviewConfirmedClicked({
                section: currentKey === 'M6' ? 'clinic_setup' : 'your_intake',
              });
            }
            void handleCompletedPageContinue(currentKey === 'M6' ? 'M6' : 'P6');
          }}
          onBack={goBack}
          errorMessage={
            completionErrorPageKey === (currentKey === 'M6' ? 'M6' : 'P6')
              ? 'Your review acknowledgement could not be saved. Try again.'
              : undefined
          }
          primaryLabel={
            context.kind === 'admin'
              ? context.userId
                ? `Mark reviewed on behalf of ${context.targetLabel ?? context.providerEmail ?? 'selected provider'}`
                : 'Mark reviewed'
              : 'Looks good'
          }
          disabledReason={
            isDocumentProcessing
              ? 'Wait for your documents to finish processing before continuing.'
              : isSavingAdminChanges || isSavingPageCompletion
                ? 'Wait for your changes to finish saving before continuing.'
                : adminSaveErrorCount > 0
                  ? 'Resolve the changes that could not be saved before continuing.'
                  : undefined
          }
        />
      )}

      {!isAggregateView && currentKey === 'X1' && completionPageReachable && (
        <IntakeCompletionPage
          onGoToDashboard={isAdmin ? undefined : handleGoToDashboard}
          canVerifyIntake={canVerifyIntake && Boolean(dashboard?.journey_status?.submitted_at)}
          verifiedAt={dashboard?.journey_status?.verified_at}
          onVerifyIntake={() => handleVerifyAdminIntake()}
          onDiscardVerification={() => handleDiscardAdminVerification()}
          isVerifyingIntake={isVerifyingAdminIntake}
          isDiscardingVerification={isDiscardingAdminVerification}
          verifyBlockedReason={
            hasUnsavedAdminChanges || isSavingAdminChanges || adminSaveErrorCount > 0
              ? 'Save all Admin changes before verifying.'
              : undefined
          }
        />
      )}

      {!isAggregateView && currentKey in DOCUMENT_PAGES && (
        <IntakeDocumentsPage
          title={DOCUMENT_PAGES[currentKey].title}
          sub={DOCUMENT_PAGES[currentKey].sub}
          dropHint={DOCUMENT_PAGES[currentKey].hint}
          documents={foundDocuments}
          onDismissFailed={dismissByArtifactId}
          getDocumentUrl={getDocumentUrl}
          isDragging={isDragging}
          isProcessing={isDocumentProcessing}
          onFiles={handleFiles}
          landedSummary={landedSummary}
          onReview={
            firstPageWithSuggestions ? () => navigateToPage(firstPageWithSuggestions) : undefined
          }
          reviewLabel={
            firstPageWithSuggestions ? `Review ${pageLabel(firstPageWithSuggestions)} →` : undefined
          }
          groups={currentGroups}
          readOnly={isAggregateView}
          onHelpAction={handleHelpAction}
          onContinue={goNext}
          onBack={goBack}
          nextLabel={nextLabel}
          uploadingCount={uploadingFileCount}
          showRowDownload={isAdmin}
          requirements={documentRequirementStatuses(
            currentFields,
            dashboard?.items ?? [],
            userId,
            documentByReferenceId,
          )}
        />
      )}

      {!isAggregateView &&
        !['K1', 'F1', 'M5', 'M6', 'P6', 'X1'].includes(currentKey) &&
        !(currentKey in DOCUMENT_PAGES) && (
          <>
            <IntakeFieldsPage
              title={currentLabel}
              fields={currentFields}
              groups={currentGroups}
              suggestions={documentSuggestions}
              onAcceptSuggestion={handleAcceptSuggestion}
              onDiscardSuggestion={handleDiscardSuggestion}
              onContinue={goNext}
              onBack={goBack}
              nextLabel={nextLabel}
              draftValues={draftValues}
              acknowledgedValues={acknowledgedValues}
              hasAcknowledgedValues={hasAcknowledgedValues}
              onDraftChange={handleDraftChange}
              onPersistField={handlePersistField}
              repeatRecordsByGroup={repeatRecordsByGroup}
              onPersistRepeatGroup={handlePersistRepeatGroup}
              onHelpAction={handleHelpAction}
              maskedSensitiveValues={maskedSensitiveValues}
              disabledReasons={disabledReasons}
              saveMode={isAdmin ? 'explicit' : 'autosave'}
              isSaving={isSavingAdminChanges}
              saveErrorCount={adminSaveErrorCount}
              onSaveChanges={isAdmin ? () => void handleSaveAdminChanges(currentFields) : undefined}
            />
          </>
        )}

      {(isDragging || isUploadInFlight) && (
        <ArtifactDropOverlay
          subtitle={isUploadInFlight ? 'Keep this page open while the upload finishes.' : undefined}
          isUploading={isUploadInFlight}
          uploadingMessage={`Uploading ${uploadingFileCount} file${
            uploadingFileCount === 1 ? '' : 's'
          }`}
        />
      )}
    </IntakeShell>
  );
}
